import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { ethers } from "ethers";
import { toast } from "react-hot-toast";
import { AnimatePresence, motion } from "framer-motion";
import {
    FiRotateCcw,
    FiLock,
    FiUnlock,
    FiCopy,
    FiShare2,
    FiX,
} from "react-icons/fi";
import { getTokenBySymbol } from "../tokens";
import { useWallet } from "../hooks/useWallet";
import { useRPCProvider } from "../hooks/useRPCProvider";
import { CONTRACT_ADDRESSES, CONTRACT_ABIS, API_BASE } from "../constants/index";

import dayjs from "dayjs";
import { useTranslation } from "../hooks/useTranslation";
import nft1000Img from "../images/staking-pokemon-card-1000usd.png";
import nft10000Img from "../images/staking-pokemon-card-10000usd.png";
import RedeemIcon from "../images/tcg-icon-claim-nft.png";
import StakingSuccessIcon from "../images/tcg-icon-done-submitted.png";
import ImgTotalStaked from "../images/icon-staking-staked.png";
import ImgClaimable from "../images/icon-staking-claimable.png";
import Img3dTotalStaked from "../images/total-staked-img.png";
import IconHistoryStaked from "../images/icon-stake-history.png";
import IconTokenUsdt from "../images/tokens-icon-usdt.png";
import ImgClaimCheck from "../images/rewards-claimed-check.png";
import ImgStakingActive from "../images/staking-active-img.png";

export default function Staking({ variant = "staking" } = {}) {
    const { t } = useTranslation();
    const { account, isConnected, isCorrectNetwork } = useWallet();
    const { executeWithFailover, getWalletSigner, getProvider } = useRPCProvider();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const isYieldPage = variant === "yield";
    const MIN_YIELD_STAKE_AMOUNT = 30;
    const MIN_YIELD_CLAIM_AMOUNT = 30;

    // State management
    const [stakeAmount, setStakeAmount] = useState("");
    const [ticketId, setTicketId] = useState("");
    const [stakeDays, setStakeDays] = useState("30");
    const [referralAddress, setReferralAddress] = useState("");
    const [usdtBalance, setUsdtBalance] = useState("0");
    const [isLoadingBalance, setIsLoadingBalance] = useState(false);
    const [isLoadingStake, setIsLoadingStake] = useState(false);
    const [isLoadingUnstake, setIsLoadingUnstake] = useState(false);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    const [, setStakeHistory] = useState([]);
    const [totalStaked, setTotalStaked] = useState(0);
    const [userStakes, setUserStakes] = useState([]);
    const [currentTime, setCurrentTime] = useState(Date.now());
    const [maxStakeAmount, setMaxStakeAmount] = useState("0");
    const [isReferralBound, setIsReferralBound] = useState(false);
    const [showAllStakes, setShowAllStakes] = useState(false);
    const [loadingProgress, setLoadingProgress] = useState("");
    const [stakeSuccess, setStakeSuccess] = useState(false);
    const [successData, setSuccessData] = useState(null);
    const [pendingRewards, setPendingRewards] = useState("0.00 USDT");
    const [claimableRewards, setClaimableRewards] = useState("0.00 USDT");
    const claimableRewardValue = String(claimableRewards || "0.00").replace(/\s*USDT\s*$/i, "");
    const [rewardCapProgress, setRewardCapProgress] = useState(0); // 0–100, progress toward 3x max cap
    const [totalEarnedRewards, setTotalEarnedRewards] = useState(0); // for "earned / cap" display
    const [isApproving, setIsApproving] = useState(false);
    const [showApproveModal, setShowApproveModal] = useState(false);
    const [showStakeModal, setShowStakeModal] = useState(false);
    const [approvalStatus, setApprovalStatus] = useState(null);
    const [approvalError, setApprovalError] = useState(null);
    const [userROIAndCapLive, setUserROIAndCapLive] = useState(null);
    const [ranking, setRanking] = useState(0);
    const [isLoadingClaimRewards, setIsLoadingClaimRewards] = useState(false);
    const [showClaimModal, setShowClaimModal] = useState(false);
    const [abnbUsdtBalance, setAbnbUsdtBalance] = useState("0");
    const [vtBalance, setVtBalance] = useState("0");
    const [gramBalance, setGramBalance] = useState("0");
    const [atokenBalance, setAtokenBalance] = useState("0");
    const [claimEnabled, setClaimEnabled] = useState(false);
    const [isLoadingClaimStatus, setIsLoadingClaimStatus] = useState(false);
    const [isRestaking, setIsRestaking] = useState(false);
    const [showRestakeModal, setShowRestakeModal] = useState(false);
    const [showRestakeSuccessModal, setShowRestakeSuccessModal] = useState(false);
    const [showAtokenWithdrawalModal, setShowAtokenWithdrawalModal] = useState(false);
    const [isWithdrawingAtoken, setIsWithdrawingAtoken] = useState(false);
    const [selectedToken, setSelectedToken] = useState(isYieldPage ? "VT Balance" : "USDT"); // "USDT", "aBnBusdt", or "VT Balance"
    const [showProcessClaimModal, setShowProcessClaimModal] = useState(false);
    const [processClaimStep, setProcessClaimStep] = useState(1); // 1 = Processing, 2 = Success
    const [showProcessStakeModal, setShowProcessStakeModal] = useState(false);
    const [processStakeStep, setProcessStakeStep] = useState(1); // 1 = Processing, 2 = Success
    const [yieldStakeSummary, setYieldStakeSummary] = useState({ totalStake: "0.00", records: [] });
    const [isLoadingYieldSummary, setIsLoadingYieldSummary] = useState(false);
    const [yieldBonusList, setYieldBonusList] = useState([]);
    const [isLoadingYieldBonusList, setIsLoadingYieldBonusList] = useState(false);

    // Ref to prevent multiple simultaneous loading calls
    const isLoadingRef = useRef(false);

    // Ref to track if we've already checked referral for this account
    const hasCheckedReferral = useRef(false);

    useEffect(() => {
        if (isYieldPage && selectedToken !== "VT Balance") {
            setSelectedToken("VT Balance");
        }
    }, [isYieldPage, selectedToken]);

    // Get USDT token info
    const usdtToken = getTokenBySymbol("USDT");

    const formatBalanceForDisplay = useCallback((value, decimals = 6) => {
        const n = Number(value || 0);
        if (!Number.isFinite(n)) return (0).toFixed(decimals);
        return n.toFixed(decimals);
    }, []);

    const fetchYieldClaimStatus = useCallback(async () => {
        if (!isYieldPage || !account || !isConnected) {
            setClaimEnabled(false);
            return;
        }
        setIsLoadingClaimStatus(true);
        try {
            const response = await fetch(`${API_BASE}/publicApi/yieldClaimStatus`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ walletAddress: account }),
            });
            const result = await response.json().catch(() => ({}));
            setClaimEnabled(result.success === true && result.enabled === true);
        } catch (error) {
            console.warn("Error fetching Yield Claim status:", error);
            setClaimEnabled(false);
        } finally {
            setIsLoadingClaimStatus(false);
        }
    }, [account, isConnected, isYieldPage]);

    useEffect(() => {
        fetchYieldClaimStatus();
    }, [fetchYieldClaimStatus]);

    // VT + GRAM staking requires each stake to be funded 50% by VT and 50% by GRAM.
    // Example: VT 100 + GRAM 10 => max stakeable amount min(100, 10) * 2 = 20.
    const vtGramStakeableBalance = useMemo(() => {
        const vt = Number(vtBalance || 0);
        const gram = Number(gramBalance || 0);
        if (!Number.isFinite(vt) || !Number.isFinite(gram)) return 0;
        return Math.max(0, Math.min(vt, gram) * 2);
    }, [vtBalance, gramBalance]);

    const yieldHalfStakeAmount = useMemo(() => {
        const amount = Number(String(stakeAmount || "").replace(/,/g, "").trim());
        if (!Number.isFinite(amount) || amount <= 0) return "0.00";
        const half = amount / 2;
        return half.toLocaleString("en-US", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 6,
        });
    }, [stakeAmount]);

    const normalizedStakeAmountValue = useMemo(() => {
        const amount = Number(String(stakeAmount || "").replace(/,/g, "").trim());
        return Number.isFinite(amount) ? amount : 0;
    }, [stakeAmount]);

    const isYieldStakeBelowMinimum = isYieldPage && stakeAmount && normalizedStakeAmountValue > 0 && normalizedStakeAmountValue < MIN_YIELD_STAKE_AMOUNT;
    const isYieldStakeButtonDisabled = isYieldPage && (!Number.isFinite(normalizedStakeAmountValue) || normalizedStakeAmountValue < MIN_YIELD_STAKE_AMOUNT);

    // Stake days options with rates
    const getStakeDaysOptions = () => [
        {
            days: 1,
            rate: 0.3,
            label: `1 ${t("staking.days")} - 0.3%`,
            index: 0,
        },
        { days: 7, rate: 1.0, label: `7 ${t("staking.days")} - 1%`, index: 1 },
        {
            days: 30,
            rate: 1.5,
            label: `30 ${t("staking.days")} - 1.5%`,
            index: 2,
        },
    ];


    // Get stake index from days
    const getStakeIndex = (days) => {
        const option = getStakeDaysOptions().find(
            (opt) => opt.days === parseInt(days)
        );
        return option ? option.index : 0;
    };

    const fetchYieldStakeSummary = useCallback(async () => {
        if (!isYieldPage || !account || !isConnected) {
            setYieldStakeSummary({ totalStake: "0.00", records: [] });
            return;
        }

        setIsLoadingYieldSummary(true);
        try {
            const response = await fetch(`${API_BASE}/publicApi/getYieldStakingRewards`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ userAddress: account }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || "Failed to load yield summary");
            }
            const totalStake = result.total_stake ?? result.total ?? "0.00";
            const records = result.stake_history ?? result.records ?? [];
            setYieldStakeSummary({
                totalStake: totalStake == null || totalStake === "" ? "0.00" : String(totalStake),
                records: Array.isArray(records) ? records : [],
            });
        } catch (error) {
            console.warn("Error fetching yield stake summary:", error);
            setYieldStakeSummary({ totalStake: "0.00", records: [] });
        } finally {
            setIsLoadingYieldSummary(false);
        }
    }, [isYieldPage, account, isConnected]);

    useEffect(() => {
        fetchYieldStakeSummary();
    }, [fetchYieldStakeSummary]);

    const fetchYieldBonusList = useCallback(async () => {
        if (!isYieldPage || !account || !isConnected) {
            setYieldBonusList([]);
            return;
        }

        setIsLoadingYieldBonusList(true);
        try {
            const response = await fetch(`${API_BASE}/publicApi/getYieldBonusList`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ walletAddress: account, page: 1, limit: 10 }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || "Failed to load yield bonus list");
            }
            setYieldBonusList(Array.isArray(result.data) ? result.data : []);
        } catch (error) {
            console.warn("Error fetching yield bonus list:", error);
            setYieldBonusList([]);
        } finally {
            setIsLoadingYieldBonusList(false);
        }
    }, [isYieldPage, account, isConnected]);

    useEffect(() => {
        fetchYieldBonusList();
    }, [fetchYieldBonusList]);

    const formatYieldBonusAmount = useCallback((value) => {
        const n = Number(value || 0);
        if (!Number.isFinite(n)) return "0.00";
        return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 6 });
    }, []);

    const formatYieldBonusPercent = useCallback((value) => {
        const n = Number(value || 0);
        if (!Number.isFinite(n)) return "0%";
        return `${n.toLocaleString("en-US", { maximumFractionDigits: 4 })}%`;
    }, []);


    // Check if stake can be unstaked based on time
    const canUnstake = (stake) => {
        if (stake.status === true) {
            return false; // Already unstaked
        }

        const stakeTime = Number(stake.stakeTime);
        const stakeIndex = Number(stake.stakeIndex); // Convert to number in case it's BigInt or string

        // Get stake period in days
        const stakeOption = getStakeDaysOptions().find(
            (opt) => opt.index === stakeIndex
        );

        if (!stakeOption) {
            console.log(`No stake option found for index ${stakeIndex}`);
            return false;
        }

        const stakePeriodDays = stakeOption.days;
        const stakePeriodMs = stakePeriodDays * 24 * 60 * 60 * 1000; // Convert to milliseconds
        const stakeStartTime = stakeTime * 1000; // Convert to milliseconds

        const timeElapsed = currentTime - stakeStartTime;
        const canUnstakeNow = timeElapsed >= stakePeriodMs;

        // console.log("Time calculation:");
        // console.log("  Stake period days:", stakePeriodDays);
        // console.log("  Stake period ms:", stakePeriodMs);
        // console.log("  Stake start time:", stakeStartTime);
        // console.log("  Current time:", currentTime);
        // console.log("  Time elapsed:", timeElapsed);
        // console.log("  Can unstake:", canUnstakeNow);

        return canUnstakeNow;
    };

    // Get time remaining until unstake is available
    const getTimeUntilUnstake = (stake) => {
        if (stake.status === true) {
            return null; // Already unstaked
        }

        const stakeTime = Number(stake.stakeTime);
        const stakeIndex = Number(stake.stakeIndex); // Convert to number in case it's BigInt or string

        const stakeOption = getStakeDaysOptions().find(
            (opt) => opt.index === stakeIndex
        );
        if (!stakeOption) {
            return null;
        }

        const stakePeriodDays = stakeOption.days;
        const stakePeriodMs = stakePeriodDays * 24 * 60 * 60 * 1000;
        const stakeStartTime = stakeTime * 1000;

        const timeElapsed = currentTime - stakeStartTime;
        const timeRemaining = stakePeriodMs - timeElapsed;

        if (timeRemaining <= 0) {
            return null; // Can unstake now
        }

        // Convert to days, hours, minutes
        const days = Math.floor(timeRemaining / (24 * 60 * 60 * 1000));
        const hours = Math.floor(
            (timeRemaining % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000)
        );
        const minutes = Math.floor(
            (timeRemaining % (60 * 60 * 1000)) / (60 * 1000)
        );

        if (days > 0) {
            return `${days}d ${hours}h`;
        } else if (hours > 0) {
            return `${hours}h ${minutes}m`;
        } else {
            return `${minutes}m`;
        }
    };

    // Claim button: show only when at least one stake's reward period has finished (calculate days done)
    const canClaim = useMemo(() => {
        const now = currentTime;
        return userStakes.some((stake) => {
            if (stake.status === true) return false;
            const stakeTime = Number(stake.stakeTime);
            const opt = getStakeDaysOptions().find(
                (o) => o.index === Number(stake.stakeIndex)
            );
            if (!opt) return false;
            const periodMs = opt.days * 24 * 60 * 60 * 1000;
            return now - stakeTime * 1000 >= periodMs;
        });
    }, [userStakes, currentTime]);

    // Get user stakes from contract with batch loading and timeout protection
    const getUserStakes = useCallback(async () => {
        if (!account || !isConnected) {
            return [];
        }

        try {
            const stakes = await executeWithFailover(async (provider) => {
                const contract = new ethers.Contract(
                    CONTRACT_ADDRESSES.STAKING,
                    CONTRACT_ABIS.STAKING,
                    provider
                );

                // Get stake count first
                const stakeCount = await contract.stakeCount(account);

                // Handle BigInt conversion safely
                let stakeCountNum;
                try {
                    stakeCountNum = Number(stakeCount);
                    if (stakeCountNum > Number.MAX_SAFE_INTEGER) {
                        stakeCountNum = parseInt(stakeCount.toString());
                    }
                } catch (error) {
                    console.error("Error converting stake count:", error);
                    stakeCountNum = parseInt(stakeCount.toString());
                }

                if (isNaN(stakeCountNum) || stakeCountNum < 0) {
                    console.error("Invalid stake count:", stakeCount);
                    return [];
                }

                // Get individual stakes with batch loading
                const stakesArray = [];
                const batchSize = 3; // Smaller batch size for better reliability
                const maxRetries = 2; // Retry failed requests

                for (let i = 0; i < stakeCountNum; i += batchSize) {
                    const batchEnd = Math.min(i + batchSize, stakeCountNum);

                    const batchPromises = [];
                    for (let j = i; j < batchEnd; j++) {
                        batchPromises.push(
                            (async () => {
                                let retries = 0;
                                while (retries < maxRetries) {
                                    try {
                                        const [stakeInfo, rewardResult] =
                                            await Promise.all([
                                                contract.userStakeRecord(
                                                    account,
                                                    j
                                                ),
                                                contract
                                                    .rewardOfSlot(account, j)
                                                    .catch(() => 0),
                                            ]);

                                        return {
                                            index: j,
                                            stakeTime: stakeInfo.stakeTime,
                                            amount: stakeInfo.amount,
                                            status: stakeInfo.status,
                                            stakeIndex: stakeInfo.stakeIndex,
                                            reward: rewardResult.toString(),
                                        };
                                    } catch (error) {
                                        retries++;
                                        console.error(
                                            `Error getting stake ${j} (attempt ${retries}):`,
                                            error
                                        );
                                        if (retries >= maxRetries) {
                                            console.error(
                                                `Failed to get stake ${j} after ${maxRetries} attempts`
                                            );
                                            return null;
                                        }
                                        // Wait before retry
                                        await new Promise((resolve) =>
                                            setTimeout(resolve, 1000)
                                        );
                                    }
                                }
                                return null;
                            })()
                        );
                    }

                    // Wait for batch to complete
                    const batchResults = await Promise.all(batchPromises);
                    const validStakes = batchResults.filter(
                        (stake) => stake !== null
                    );
                    stakesArray.push(...validStakes);

                    // Update progress
                    setLoadingProgress(
                        `${t("staking.loading")} ${stakesArray.length
                        } of ${stakeCountNum} ${t("staking.stakes")}...`
                    );

                    // Small delay between batches to prevent overwhelming the RPC
                    if (batchEnd < stakeCountNum) {
                        await new Promise((resolve) =>
                            setTimeout(resolve, 500)
                        );
                    }
                }

                return stakesArray;
            });

            return stakes;
        } catch (error) {
            console.error("Error fetching user stakes:", error);
            return [];
        }
    }, [account, isConnected, executeWithFailover]);

    // Check if user has existing referral (upline) via Referral contract
    const checkExistingReferral = useCallback(async () => {
        if (!account || !isConnected) {
            setIsReferralBound(false);
            setReferralAddress("");
            return;
        }

        const ZERO = "0x0000000000000000000000000000000000000000";
        try {
            const { isBound, referralAddr } = await executeWithFailover(async (provider) => {
                const referralContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.REFERRALS,
                    CONTRACT_ABIS.REFERRALS,
                    provider
                );
                const bound = await referralContract.isBindReferral(account);
                if (!bound) return { isBound: false, referralAddr: null };
                const ref = await referralContract.getReferral(account);
                const addr = ref && typeof ref === "string" ? ref : (ref?.toString?.() || String(ref));
                const valid = addr && addr.toLowerCase() !== ZERO.toLowerCase();
                return { isBound: !!valid, referralAddr: valid ? addr : null };
            });

            if (isBound && referralAddr) {
                setIsReferralBound(true);
                setReferralAddress(referralAddr);
            } else {
                setIsReferralBound(false);
                setReferralAddress("");
            }
        } catch (error) {
            console.error("Error checking existing referral:", error);
            setIsReferralBound(false);
            setReferralAddress("");
        }
    }, [account, isConnected, executeWithFailover]);

    // Yield-only: bind the referral on-chain before the backend staking API writes the stake.
    // This avoids creating a Yield stake without an on-chain referral when the user rejects the wallet transaction.
    const bindYieldReferralIfNeeded = useCallback(async (uniqueId) => {
        if (isReferralBound) return false;
        if (!account || !ethers.isAddress(referralAddress)) {
            throw new Error(t("validation.pleaseEnterReferralAddress") || "Please enter a valid referral address");
        }

        const normalizedAccount = account.toLowerCase();
        const normalizedReferral = referralAddress.toLowerCase();
        const ZERO = "0x0000000000000000000000000000000000000000";
        if (normalizedReferral === ZERO || normalizedReferral === normalizedAccount) {
            throw new Error(t("validation.pleaseEnterReferralAddress") || "Please enter a valid referral address");
        }

        // Re-check immediately before requesting a signature so a stale page state never submits a duplicate bind.
        const alreadyBound = await executeWithFailover(async (provider) => {
            const referralContract = new ethers.Contract(
                CONTRACT_ADDRESSES.REFERRALS,
                CONTRACT_ABIS.REFERRALS,
                provider
            );
            return referralContract.isBindReferral(account);
        });
        if (alreadyBound) {
            await checkExistingReferral();
            return false;
        }

        const toastId = "bind-yield-referral";
        toast.loading(t("staking.bindingUpline") || "Binding upline...", { id: toastId });
        try {
            const signRes = await fetch(`${API_BASE}/publicApi/sign`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    uniqueId: String(uniqueId),
                    callerAddress: account,
                    contractAddress: CONTRACT_ADDRESSES.REFERRALS,
                    userAddress: account,
                    referralAddress,
                }),
            });
            const signJson = await signRes.json().catch(() => ({}));
            if (!signRes.ok || !signJson?.success) {
                throw new Error(signJson?.error || signJson?.message || t("staking.signFailed") || "Failed to get signature for binding");
            }

            const normalizeHex32 = (value) => {
                if (!value) return ethers.ZeroHash;
                const hex = String(value).trim();
                return hex.startsWith("0x") ? hex : `0x${hex}`;
            };
            const vNum = typeof signJson.v === "string" ? parseInt(signJson.v, 10) : Number(signJson.v);
            if (!Number.isInteger(vNum)) {
                throw new Error(t("staking.signFailed") || "Failed to get signature for binding");
            }

            const signer = await getWalletSigner();
            const referralContract = new ethers.Contract(
                CONTRACT_ADDRESSES.REFERRALS,
                CONTRACT_ABIS.REFERRALS,
                signer
            );
            const bindTx = await referralContract.permitBindReferral(
                String(uniqueId),
                account,
                referralAddress,
                vNum,
                normalizeHex32(signJson.r),
                normalizeHex32(signJson.s)
            );
            const receipt = await bindTx.wait();
            if (receipt?.status === 0) {
                throw new Error("Referral binding transaction reverted");
            }

            toast.success(t("staking.referralBound") || "Referral bound successfully", { id: toastId });
            await checkExistingReferral();
            return true;
        } catch (error) {
            toast.dismiss(toastId);
            throw error;
        }
    }, [account, referralAddress, isReferralBound, executeWithFailover, checkExistingReferral, getWalletSigner, t]);

    // Fetch USDT balance with RPC failover
    const fetchUsdtBalance = useCallback(async () => {
        if (!account || !isConnected || !usdtToken || !isCorrectNetwork) {
            setUsdtBalance("0");
            setAbnbUsdtBalance("0");
            setVtBalance("0");
            setGramBalance("0");
            setAtokenBalance("0");
            return;
        }

        setIsLoadingBalance(true);
        try {
            // Fetch GRAM and VT balances from backend API
            try {
                const res = await fetch(`${API_BASE}/publicApi/getBalance`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ wallet_address: account }),
                });
                const result = await res.json().catch(() => ({}));
                if (result.success && result.gram_balance !== undefined) {
                    setGramBalance(result.gram_balance.toString());
                } else {
                    setGramBalance("0");
                }

                if (result.success && result.vt_balance !== undefined) {
                    setVtBalance(result.vt_balance.toString());
                } else {
                    setVtBalance("0");
                }

                if (result.success && result.atoken_balance !== undefined) {
                    setAtokenBalance(result.atoken_balance.toString());
                } else {
                    setAtokenBalance("0");
                }

            } catch (apiErr) {
                console.warn("Error fetching GRAM balance inside Staking.js:", apiErr);
                setGramBalance("0");
                setVtBalance("0");
                setAtokenBalance("0");
            }

            const { balance, abnbBalance } = await executeWithFailover(async (provider) => {
                const USDTContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.USDT,
                    CONTRACT_ABIS.USDT,
                    provider
                );

                const SwapContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.SWAP,
                    CONTRACT_ABIS.SWAP,
                    provider
                );

                const balanceResult = await USDTContract.balanceOf(account);
                const abnbBalanceResult = await SwapContract.balanceOf(account);

                const userAbnbBalance = ethers.formatUnits(abnbBalanceResult, 18);
                const userUSDBalance = ethers.formatUnits(balanceResult, 18);

                const fixedUserAbnbBalance = userAbnbBalance.includes('.')
                    ? userAbnbBalance.slice(0, userAbnbBalance.indexOf('.') + 7)
                    : userAbnbBalance;

                const fixedUserUSDBalance = userUSDBalance.includes('.')
                    ? userUSDBalance.slice(0, userUSDBalance.indexOf('.') + 7)
                    : userUSDBalance;

                return { balance: fixedUserUSDBalance, abnbBalance: fixedUserAbnbBalance };
            });

            setUsdtBalance(balance);
            setAbnbUsdtBalance(abnbBalance);
        } catch (error) {
            if (error?.code === "CALL_EXCEPTION") {
                console.warn("USDT balance fetch skipped until provider/network is ready.");
            } else {
                console.error("Error fetching USDT balance:", error);
            }
            setUsdtBalance("0");
        } finally {
            setIsLoadingBalance(false);
        }
    }, [account, isConnected, isCorrectNetwork, usdtToken, executeWithFailover]);

    // Fetch balance from publicApi/getBalance (pass wallet_address)
    // const fetchBalanceFromApi = useCallback(async () => {
    //     if (!account) {
    //         setApiBalance(null);
    //         return;
    //     }
    //     try {
    //         const res = await fetch(GET_BALANCE_API, {
    //             method: "POST",
    //             headers: { "Content-Type": "application/json" },
    //             body: JSON.stringify({ wallet_address: account }),
    //         });
    //         const data = await res.json().catch(() => ({}));

    //         if (res.ok && data != null) {
    //             const bal = data.balance ?? data.amount ?? data.data;
    //             setApiBalance(bal != null ? String(bal) : null);
    //         } else {
    //             setApiBalance(null);
    //         }
    //     } catch (e) {
    //         setApiBalance(null);
    //     }
    // }, [account]);

    // Save stake record

    const saveStakeRecord = useCallback((amount, ticketId, txHash) => {
        const record = {
            id: Date.now(),
            amount: parseFloat(amount),
            ticketId: ticketId,
            txHash,
            timestamp: new Date().toISOString(),
            status: "staked",
            type: "stake",
        };

        const history = JSON.parse(
            localStorage.getItem("stakeHistory") || "[]"
        );
        history.unshift(record);
        localStorage.setItem("stakeHistory", JSON.stringify(history));

        setStakeHistory(history);
        setTotalStaked((prev) => prev + parseFloat(amount));
    }, []);

    // Handle stake
    // const handleStake = async () => {
    //     if (!account) {
    //         toast.error(t("validation.connectWalletFirst"));
    //         return;
    //     }

    //     const normalizedStakeAmount = Number(String(stakeAmount).replace(/,/g, ""));
    //     if (Number.isNaN(normalizedStakeAmount) || normalizedStakeAmount <= 0) {
    //         toast.error(t("validation.pleaseEnterValidStakeAmount"));
    //         return;
    //     }

    //     if (normalizedStakeAmount < 100) {
    //         toast.error(t("validation.pleaseEnterValidStakeAmount"));
    //         return;
    //     }

    //     if (normalizedStakeAmount > 10000){
    //         toast.error(t("validation.maxStakeAmount", {
    //             maxStakeAmount: 10000,
    //         }));
    //         return;
    //     }

    //     if (!referralAddress) {
    //         toast.error(t("validation.pleaseEnterReferralAddress") || "Please enter referral address");
    //         return;
    //     }

    //     setIsLoadingStake(true);

    //     try {
    //         // Generate a random uniqueId and ensure it has not been used on-chain
    //         const uniqueId = await generateUniqueId();

    //         const res = await fetch(STAKING_API, {
    //             method: "POST",
    //             headers: { "Content-Type": "application/json" },
    //             body: JSON.stringify({
    //                 ref_address: referralAddress,
    //                 address: account,
    //                 amount: normalizedStakeAmount,
    //                 uniqueId: uniqueId,
    //             }),
    //         });
    //         const result = await res.json().catch(() => ({}));
    //         if (!result.success) {
    //             toast.error(result.error);
    //             setIsLoadingStake(false);
    //             return;
    //         }else{
    //             // Auto bind referral via permitBindReferral using backend sign API (if not yet bound)
    //             if (!isReferralBound && referralAddress && ethers.isAddress(referralAddress)) {
    //                 toast.loading(t("staking.bindingUpline") || "Binding upline...", {
    //                     id: "bind-referral",
    //                 });

    //                 try {
    //                     // Call sign API to get v, r, s (backend will build full_msg internally)
    //                     const signRes = await fetch(`${API_BASE}/publicApi/sign`, {
    //                         method: "POST",
    //                         headers: { "Content-Type": "application/json" },
    //                         body: JSON.stringify({
    //                             uniqueId,
    //                             callerAddress: account,
    //                             contractAddress: CONTRACT_ADDRESSES.REFERRALS,
    //                             userAddress: account,
    //                             referralAddress,
    //                         }),
    //                     });

    //                     const signJson = await signRes.json().catch(() => ({}));
    //                     if (!signRes.ok || !signJson?.success) {
    //                         toast.dismiss("bind-referral");
    //                         toast.error(
    //                             t("staking.signFailed") || "Failed to get signature for binding"
    //                         );
    //                         console.log('signJson: ', signJson);
    //                         setIsLoadingStake(false);
    //                         return;
    //                     }

    //                     const { v, r, s } = signJson;

    //                     // 规范化 v, r, s，确保符合 ethers v6 的类型要求
    //                     const vNum =
    //                         typeof v === "string"
    //                             ? parseInt(v, 10)
    //                             : Number(v);

    //                     const normalizeHex32 = (val) => {
    //                         if (!val) return ethers.ZeroHash;
    //                         let hex = String(val).trim();
    //                         if (!hex.startsWith("0x")) {
    //                             hex = "0x" + hex;
    //                         }
    //                         return hex;
    //                     };

    //                     const rHex = normalizeHex32(r);
    //                     const sHex = normalizeHex32(s);

    //                     // Use user's wallet to submit permitBindReferral with backend-provided v, r, s
    //                     const walletProvider = new ethers.BrowserProvider(window.ethereum);
    //                     const signer = await walletProvider.getSigner();

    //                     const referralContract = new ethers.Contract(
    //                         CONTRACT_ADDRESSES.REFERRALS,
    //                         CONTRACT_ABIS.REFERRALS,
    //                         signer
    //                     );

    //                     const bindTx = await referralContract.permitBindReferral(
    //                         uniqueId,
    //                         account,
    //                         referralAddress,
    //                         vNum,
    //                         rHex,
    //                         sHex
    //                     );

    //                     const bindReceipt = await bindTx.wait();
    //                     if (bindReceipt?.status === 0) {
    //                         console.log('bindReceipt: ', bindReceipt);
    //                         const bindError = new Error("Transaction reverted on-chain");
    //                         bindError.receipt = bindReceipt;
    //                         throw bindError;
    //                     }

    //                     toast.dismiss("bind-referral");
    //                     toast.success(
    //                         t("staking.referralBound") || "Upline bound successfully!"
    //                     );
    //                     setIsReferralBound(true);
    //                     localStorage.removeItem("pendingReferralAddress");
    //                 } catch (error) {
    //                     const bindTxError =
    //                         error?.reason ||
    //                         error?.shortMessage ||
    //                         error?.message ||
    //                         (error?.receipt ? "Transaction reverted on-chain" : null);

    //                     // Check if user rejected the transaction - check multiple error structures
    //                     const isUserRejected =
    //                         error?.code === 4001 ||
    //                         error?.code === "ACTION_REJECTED" ||
    //                         error?.info?.error?.code === 4001 ||
    //                         error?.data?.code === 4001 ||
    //                         String(error?.message || "").toLowerCase().includes("user rejected") ||
    //                         String(error?.message || "").toLowerCase().includes("denied transaction") ||
    //                         String(error?.message || "").toLowerCase().includes("user denied");

    //                     // If user is already bound on-chain, treat as success and continue to staking
    //                     if (
    //                         bindTxError &&
    //                         String(bindTxError).toLowerCase().includes("user already bound")
    //                     ) {
    //                         toast.dismiss("bind-referral");
    //                         toast.success(
    //                             t("staking.userAlreadyBound") || "User already bound"
    //                         );
    //                         setIsReferralBound(true);
    //                         localStorage.removeItem("pendingReferralAddress");
    //                         // Do NOT return; continue to staking API below
    //                     } else if (isUserRejected) {
    //                         toast.dismiss("bind-referral");
    //                         toast.error(
    //                             t("validation.transactionRejected") || "Transaction rejected"
    //                         );

    //                         //call cancel sign api to cancel the sign
    //                         try {
    //                             const cancelSignRes = await fetch(`${API_BASE}/publicApi/cancelSign`, {
    //                                 method: "POST",
    //                                 headers: { "Content-Type": "application/json" },
    //                                 body: JSON.stringify({ wallet_address: account, uniqueId: uniqueId }),
    //                             });

    //                             const cancelSignJson = await cancelSignRes.json().catch(() => ({}));

    //                             if (!cancelSignRes.ok || !cancelSignJson?.success) {
    //                                 console.warn('cancelSignJson: ', cancelSignJson);
    //                                 // Don't show error toast for cancel sign failure, just log it
    //                             } else {
    //                                 console.log('Signature cancelled successfully');
    //                             }
    //                         } catch (cancelError) {
    //                             console.warn('Failed to call cancelSign API:', cancelError);
    //                             // Don't show error toast, just log it
    //                         }

    //                         setIsLoadingStake(false);
    //                         return;
    //                     } else {
    //                         toast.dismiss("bind-referral");
    //                         toast.error(
    //                             bindTxError ||
    //                                 t("staking.signFailed") ||
    //                                 "Failed to get signature for binding"
    //                         );
    //                         setIsLoadingStake(false);
    //                         return;
    //                     }
    //                 }
    //             }

    //             setTimeout(() => {
    //                 toast.success(t("staking.successStaked") || "Staking successful!");

    //                 // Show success state in the same page
    //                 setSuccessData({
    //                     timestamp: new Date().toISOString(),
    //                     txHash: "0x" + "0".repeat(64), // Mock transaction hash
    //                     amount: stakeAmount,
    //                     ticketId: ticketId || "0",
    //                 });
    //                 setStakeSuccess(true);

    //                 setIsLoadingStake(false);
    //             }, 500); // 500ms delay to simulate loading
    //         }
    //     } catch (error) {
    //         toast.dismiss("bind-referral");
    //         const errorMessage =
    //             error?.reason ||
    //             error?.shortMessage ||
    //             error?.message ||
    //             (error?.receipt ? "Transaction reverted on-chain" : null);
    //         if (error?.code === 4001) {
    //             toast.error(
    //                 t("validation.transactionRejected") || "Transaction rejected"
    //             );
    //         } else {
    //             toast.error(
    //                 errorMessage ||
    //                     t("validation.stakingFailed") ||
    //                     "Staking failed"
    //             );
    //         }
    //         setIsLoadingStake(false);
    //         return;
    //     }

    //     // ========== COMMENTED OUT: ALL CONTRACT INTERACTION CODE ==========
    //     /*
    //     // All contract interaction code has been commented out for testing
    //     // Original code included:
    //     // - USDT contract approval
    //     // - Staking contract stakeWithInviter call
    //     // - Referral contract binding
    //     // - Transaction waiting and error handling
    //     */
    //     // ========== END OF COMMENTED CODE ==========

    //     // Simulate loading delay, then show success immediately (for testing)

    // };

    const formatBonusAmount = useCallback((val) => {
        if (val == null) return "0.00";
        const raw = typeof val === "bigint" ? val.toString() : (val?.toString ? val.toString() : String(val));
        const parsed = parseFloat(raw);
        if (Number.isNaN(parsed)) return "0.00";
        return (parsed / 1e18).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }, []);

    const getUserROIAndCapLive = useCallback(async () => {
        if (!account || !isConnected || !isCorrectNetwork) {
            return;
        }

        try {

            const {
                stakeAmount,
                pendingBase,
                pendingActive,
                confirmedBase,
                confirmedActive,
                totalROI,
                maxCap,
                remainingMaxCap,
                growthBonus,
                consensusBonus,
                totalActive
            } = await executeWithFailover(async (provider) => {

                const stakingContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.STAKING,
                    CONTRACT_ABIS.STAKING,
                    provider
                );

                const {
                    stakeAmount,
                    pendingBase,
                    pendingActive,
                    confirmedBase,
                    confirmedActive,
                    totalROI,
                    maxCap,
                    remainingMaxCap
                } = await stakingContract.getUserROIAndCapLive(account);

                const growthBonus = await stakingContract.userConfirmedGeneration(account);
                const consensusBonus = await stakingContract.userConfirmedCommunity(account);

                const confirmBaseMinusAmount = await stakingContract.userClaimedBaseROI(account);
                const confirmActiveMinusAmount = await stakingContract.userClaimedActiveROI(account);
                const growthBonusMinusAmount = await stakingContract.userClaimedGeneration(account);
                const consensusBonusMinusAmount = await stakingContract.userClaimedCommunity(account);

                let newConfirmBaseAmount = confirmedBase;
                let newConfirmActiveAmount = confirmedActive;
                let newGrowthBonus = growthBonus;
                let newConsensusBonus = consensusBonus;

                if (confirmBaseMinusAmount > 0) {
                    newConfirmBaseAmount = confirmedBase - confirmBaseMinusAmount;
                }
                if (confirmActiveMinusAmount > 0) {
                    newConfirmActiveAmount = confirmedActive - confirmActiveMinusAmount;
                }

                if (growthBonusMinusAmount > 0) {
                    newGrowthBonus = growthBonus - growthBonusMinusAmount;
                }
                if (consensusBonusMinusAmount > 0) {
                    newConsensusBonus = consensusBonus - consensusBonusMinusAmount;
                }

                const total_active = newGrowthBonus + newConsensusBonus;

                return {
                    stakeAmount: formatBonusAmount(stakeAmount),
                    pendingBase: formatBonusAmount(pendingBase),
                    pendingActive: formatBonusAmount(pendingActive),
                    confirmedBase: formatBonusAmount(newConfirmBaseAmount),
                    confirmedActive: formatBonusAmount(newConfirmActiveAmount),
                    totalROI: formatBonusAmount(totalROI),
                    maxCap: formatBonusAmount(maxCap),
                    remainingMaxCap: formatBonusAmount(remainingMaxCap),
                    growthBonus: formatBonusAmount(newGrowthBonus),
                    consensusBonus: formatBonusAmount(newConsensusBonus),
                    totalActive: formatBonusAmount(total_active),
                };
            });

            setUserROIAndCapLive({
                "stakeAmount": stakeAmount,
                "pendingBase": pendingBase,
                "pendingActive": pendingActive,
                "confirmedBase": confirmedBase,
                "confirmedActive": confirmedActive,
                "totalROI": totalROI,
                "maxCap": maxCap,
                "remainingMaxCap": remainingMaxCap,
                "growthBonus": growthBonus,
                "consensusBonus": consensusBonus,
                "totalActive": totalActive,
            });

        } catch (error) {
            console.error("Error getting user ROI and cap live:", error);
        }

    }, [account, isConnected, isCorrectNetwork, executeWithFailover]);

    useEffect(() => {
        getUserROIAndCapLive();
    }, [getUserROIAndCapLive]);

    // const handleStake = useCallback(async () => {
    //     if (!account) {
    //         toast.error(t("validation.connectWalletFirst"));
    //         return;
    //     }

    //     const normalizedStakeAmountStr = String(stakeAmount).replace(/,/g, "").trim();
    //     const normalizedStakeAmount = Number(normalizedStakeAmountStr);
    //     if (Number.isNaN(normalizedStakeAmount) || normalizedStakeAmount <= 0) {
    //         toast.error(t("validation.pleaseEnterValidStakeAmount"));
    //         return;
    //     }

    //     if (selectedToken == "USDT") {

    //         if (normalizedStakeAmount < 100) {
    //             toast.error(t("validation.pleaseEnterValidStakeAmount"));
    //             return;
    //         }

    //         if (normalizedStakeAmount > 10000) {
    //             toast.error(t("validation.maxStakeAmount", {
    //                 maxStakeAmount: 10000,
    //             }));
    //             return;
    //         }
    //     }

    //     if (!referralAddress) {
    //         toast.error(t("validation.pleaseEnterReferralAddress") || "Please enter referral address");
    //         return;
    //     }

    //     // const normalizedStakeAmountWei = ethers.parseUnits(normalizedStakeAmountStr, 18);
    //     // Sanitize: remove trailing dot, cap to 18 decimal places to avoid ethers parseUnits errors
    //     const raw = String(normalizedStakeAmountStr ?? "").trim().replace(/,/g, "");

    //     if (!/^\d+(\.\d+)?$/.test(raw)) {
    //         throw new Error("Invalid amount format");
    //     }

    //     const normalizedStakeAmountWei = ethers.parseUnits(raw, 18);

    //     // Close confirmation modal once user confirmed staking.
    //     setShowStakeModal(false);
    //     setIsLoadingStake(true);
    //     // Capture the toast id returned by react-hot-toast for reliable dismissing.
    //     const toastId = toast.loading(t("staking.stakingUSDT") || "Staking USDT...", {
    //         id: "staking-usdt",
    //     });

    //     let walletType = 0;
    //     if (selectedToken == "aBnBusdt") {
    //         walletType = 1;
    //     }

    //     try {
    //         setProcessStakeStep(1);

    //         const signer = await getWalletSigner();

    //         const stakingContract = new ethers.Contract(
    //             CONTRACT_ADDRESSES.STAKING,
    //             CONTRACT_ABIS.STAKING,
    //             signer
    //         );

    //         const stakeTx = await stakingContract.stake(normalizedStakeAmountWei, referralAddress, walletType);
    //         await stakeTx.wait();

    //         toast.dismiss(toastId);
    //         toast.success(t("staking.successStaked") || "Staking successful!");

    //         setIsLoadingStake(false);
    //         getUserROIAndCapLive();
    //         setShowProcessStakeModal(true);
    //         setTimeout(() => {
    //             setProcessStakeStep(2);
    //         }, 600); // UI Simulation

    //     } catch (error) {
    //         toast.dismiss(toastId);
    //         setShowStakeModal(false);
    //         console.error("Stake error:", error);
    //         const errorMessage =
    //             error?.reason ||
    //             error?.shortMessage ||
    //             error?.message ||
    //             (error?.receipt ? "Transaction reverted on-chain" : null);
    //         if (error?.code === 4001) {
    //             toast.error(
    //                 t("validation.transactionRejected") || "Transaction rejected"
    //             );
    //         } else {
    //             toast.error(
    //                 errorMessage ||
    //                 t("validation.stakingFailed") ||
    //                 "Staking failed"
    //             );
    //         }
    //         setIsLoadingStake(false);
    //         return;
    //     }
    // }, [account, getWalletSigner, stakeAmount, referralAddress, t, getUserROIAndCapLive, selectedToken]);

    // Helper: safely convert a user-typed amount string to wei BigInt

    const handleStake = useCallback(async () => {
        if (!account) {
            toast.error(t("validation.connectWalletFirst"));
            return;
        }

        const normalizedStakeAmountStr = String(stakeAmount).replace(/,/g, "").trim();
        const normalizedStakeAmount = Number(normalizedStakeAmountStr);
        if (Number.isNaN(normalizedStakeAmount) || normalizedStakeAmount <= 0) {
            toast.error(t("validation.pleaseEnterValidStakeAmount"));
            return;
        }

        if (isYieldPage && normalizedStakeAmount < MIN_YIELD_STAKE_AMOUNT) {
            toast.error(t("validation.minimumStakeAmount", { minStakeAmount: MIN_YIELD_STAKE_AMOUNT }) || `Minimum staking amount is ${MIN_YIELD_STAKE_AMOUNT}`);
            return;
        }

        if (selectedToken == "USDT") {

            if (normalizedStakeAmount < 100) {
                toast.error(t("validation.pleaseEnterValidStakeAmount"));
                return;
            }

            if (normalizedStakeAmount > 10000) {
                toast.error(t("validation.maxStakeAmount", {
                    maxStakeAmount: 10000,
                }));
                return;
            }
        }

        if (!referralAddress) {
            toast.error(t("validation.pleaseEnterReferralAddress") || "Please enter referral address");
            return;
        }

        if (isYieldPage && selectedToken === "VT Balance" && !isReferralBound) {
            const normalizedReferral = referralAddress.trim().toLowerCase();
            const ZERO = "0x0000000000000000000000000000000000000000";
            if (!ethers.isAddress(referralAddress) || normalizedReferral === ZERO || normalizedReferral === account.toLowerCase()) {
                toast.error(t("validation.pleaseEnterReferralAddress") || "Please enter a valid referral address");
                return;
            }
        }

        // const normalizedStakeAmountWei = ethers.parseUnits(normalizedStakeAmountStr, 18);
        // Sanitize: remove trailing dot, cap to 18 decimal places to avoid ethers parseUnits errors
        const raw = String(normalizedStakeAmountStr ?? "").trim().replace(/,/g, "");

        if (!/^\d+(\.\d+)?$/.test(raw)) {
            throw new Error("Invalid amount format");
        }

        const normalizedStakeAmountWei = ethers.parseUnits(raw, 18);

        // Close confirmation modal once user confirmed staking.
        setShowStakeModal(false);
        setIsLoadingStake(true);
        // Capture the toast id returned by react-hot-toast for reliable dismissing.
        const toastId = toast.loading(t("staking.stakingUSDT") || "Staking USDT...", {
            id: "staking-usdt",
        });

        let walletType = 0;
        if (selectedToken === "aBnBusdt") {
            walletType = 1;
        } else if (selectedToken === "VT Balance") {
            walletType = 2;
        }

        try {
            if (selectedToken === "VT Balance") {
                const requiredTokenAmount = normalizedStakeAmount / 2;
                const currentVtBalance = Number(vtBalance || 0);
                const currentGramBalance = Number(gramBalance || 0);

                if (currentVtBalance < requiredTokenAmount) {
                    throw new Error(`insufficient VT balance. Required: ${formatBalanceForDisplay(requiredTokenAmount)}, Available: ${formatBalanceForDisplay(currentVtBalance)}`);
                }

                if (currentGramBalance < requiredTokenAmount) {
                    throw new Error(`insufficient GRAM balance. Required: ${formatBalanceForDisplay(requiredTokenAmount)}, Available: ${formatBalanceForDisplay(currentGramBalance)}`);
                }

                const generateUniqueId = () => {
                    const now = Date.now().toString();
                    const rand = Math.floor(Math.random() * 1_000_000)
                        .toString()
                        .padStart(6, "0");
                    return now + rand;
                };

                const uniqueId = generateUniqueId();

                // For an unbound Yield wallet, bind the selected referral first. If binding fails or is rejected,
                // the backend staking API is intentionally not called.
                await bindYieldReferralIfNeeded(uniqueId);

                const response = await fetch(`${API_BASE}/publicApi/staking`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        ref_address: referralAddress,
                        address: account,
                        amount: normalizedStakeAmountStr,
                        uniqueId,
                    }),
                });
                const result = await response.json().catch(() => ({}));
                if (!response.ok || !result.success) {
                    throw new Error(result.error || result.message || "VT + GRAM staking failed");
                }
            } else {
                const signer = await getWalletSigner();

                const stakingContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.STAKING,
                    CONTRACT_ABIS.STAKING,
                    signer
                );

                const stakeTx = await stakingContract.stake(normalizedStakeAmountWei, referralAddress, walletType);
                await stakeTx.wait();
            }

            toast.dismiss(toastId);
            toast.success(t("staking.successStaked") || "Staking successful!");

            setStakeSuccess(true);
            setIsLoadingStake(false);
            getUserROIAndCapLive();
            fetchUsdtBalance();
            if (isYieldPage) {
                fetchYieldStakeSummary();
            }
            setStakeAmount('');
        } catch (error) {
            toast.dismiss(toastId);
            setShowStakeModal(false);
            console.error("Stake error:", error);
            const errorMessage =
                error?.reason ||
                error?.shortMessage ||
                error?.message ||
                (error?.receipt ? "Transaction reverted on-chain" : null);
            if (error?.code === 4001) {
                toast.error(
                    t("validation.transactionRejected") || "Transaction rejected"
                );
            } else {
                toast.error(
                    errorMessage ||
                    t("validation.stakingFailed") ||
                    "Staking failed"
                );
            }
            setIsLoadingStake(false);
            return;
        }
    }, [account, getWalletSigner, stakeAmount, referralAddress, t, getUserROIAndCapLive, selectedToken, gramBalance, vtBalance, fetchUsdtBalance, isYieldPage, fetchYieldStakeSummary, isReferralBound, bindYieldReferralIfNeeded]);

    const safeParseUnits = useCallback((amountStr, decimals = 18) => {
        let s = String(amountStr ?? "")
            .trim()
            .replace(/,/g, "")   // remove commas
            .replace(/\.$/, ""); // strip trailing dot e.g. "100."
        // Keep at most `decimals` decimal digits
        const dot = s.indexOf(".");
        if (dot !== -1) s = s.slice(0, dot + decimals + 1);
        if (!s || !/^\d+(\.\d+)?$/.test(s)) s = "0";
        return ethers.parseUnits(s, decimals);
    }, []);

    const handleCloseApprove = useCallback(() => {
        if (isApproving) return;
        setShowApproveModal(false);
        setApprovalStatus(null);
        setApprovalError(null);
    }, [isApproving]);

    const checkAllowance = useCallback(async () => {
        if (!account) {
            toast.error(t("validation.connectWalletFirst"));
            return;
        }

        const normalizedStakeAmount = Number(String(stakeAmount || "").replace(/,/g, "").trim());
        if (Number.isNaN(normalizedStakeAmount) || normalizedStakeAmount <= 0) {
            toast.error(t("validation.pleaseEnterValidStakeAmount"));
            return;
        }

        if (isYieldPage && normalizedStakeAmount < MIN_YIELD_STAKE_AMOUNT) {
            toast.error(t("validation.minimumStakeAmount", { minStakeAmount: MIN_YIELD_STAKE_AMOUNT }) || `Minimum staking amount is ${MIN_YIELD_STAKE_AMOUNT}`);
            return;
        }

        if (selectedToken === "VT Balance") {
            const requiredTokenAmount = normalizedStakeAmount / 2;
            const currentVtBalance = Number(vtBalance || 0);
            const currentGramBalance = Number(gramBalance || 0);

            if (currentVtBalance < requiredTokenAmount) {
                toast.error(`insufficient VT balance. Required: ${formatBalanceForDisplay(requiredTokenAmount)}, Available: ${formatBalanceForDisplay(currentVtBalance)}`);
                return;
            }

            if (currentGramBalance < requiredTokenAmount) {
                toast.error(`insufficient GRAM balance. Required: ${formatBalanceForDisplay(requiredTokenAmount)}, Available: ${formatBalanceForDisplay(currentGramBalance)}`);
                return;
            }
            setShowStakeModal(true);
        } else {
            const signer = await getWalletSigner();
            const tokenAddress = selectedToken === "aBnBusdt" ? CONTRACT_ADDRESSES.SWAP : CONTRACT_ADDRESSES.USDT;
            const tokenAbi = selectedToken === "aBnBusdt" ? CONTRACT_ABIS.SWAP : CONTRACT_ABIS.USDT;

            const tokenContract = new ethers.Contract(
                tokenAddress,
                tokenAbi,
                signer
            );

            const currentAllowance = await tokenContract.allowance(
                account,
                CONTRACT_ADDRESSES.STAKING
            );

            const totalAllowance = formatBonusAmount(currentAllowance);
            const approvalAmount = stakeAmount;

            if (Number(totalAllowance) < Number(approvalAmount)) {
                setApprovalStatus(null);
                setApprovalError(null);
                setShowApproveModal(true);
            } else {
                setShowStakeModal(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [account, getWalletSigner, stakeAmount, selectedToken, safeParseUnits, formatBonusAmount, handleStake, gramBalance, vtBalance, t, isYieldPage]);

    const approveStake = useCallback(async () => {
        if (!account) {
            setApprovalError(t("validation.connectWalletFirst"));
            setApprovalStatus('error');
            return;
        }

        setIsApproving(true);
        setApprovalStatus('pending');
        setApprovalError(null);

        try {
            const signer = await getWalletSigner();

            if (selectedToken === "VT Balance") {
                const usdtContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.USDT,
                    CONTRACT_ABIS.USDT,
                    signer
                );
                const vtContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.VT,
                    CONTRACT_ABIS.VT,
                    signer
                );

                const requiredAllowance = safeParseUnits((parseFloat(stakeAmount || 0) / 2).toString(), 18);

                const currentUsdtAllowance = await usdtContract.allowance(account, CONTRACT_ADDRESSES.STAKING);
                const currentVtAllowance = await vtContract.allowance(account, CONTRACT_ADDRESSES.STAKING);

                if (currentUsdtAllowance < requiredAllowance) {
                    toast.loading("Approving USDT...", { id: "approval-tx" });
                    const approveTx = await usdtContract.approve(CONTRACT_ADDRESSES.STAKING, requiredAllowance);
                    await approveTx.wait();
                    toast.success("USDT approved successfully!", { id: "approval-tx" });
                }

                const updatedVtAllowance = await vtContract.allowance(account, CONTRACT_ADDRESSES.STAKING);
                if (updatedVtAllowance < requiredAllowance) {
                    toast.loading("Approving VT...", { id: "approval-tx" });
                    const approveTx = await vtContract.approve(CONTRACT_ADDRESSES.STAKING, requiredAllowance);
                    await approveTx.wait();
                    toast.success("VT approved successfully!", { id: "approval-tx" });
                }

                setApprovalStatus('success');
                setTimeout(async () => {
                    setShowApproveModal(false);
                    setApprovalStatus(null);
                    setApprovalError(null);
                    setShowStakeModal(true);
                }, 2000);

            } else {
                const tokenAddress = selectedToken === "aBnBusdt" ? CONTRACT_ADDRESSES.SWAP : CONTRACT_ADDRESSES.USDT;
                const tokenAbi = selectedToken === "aBnBusdt" ? CONTRACT_ABIS.SWAP : CONTRACT_ABIS.USDT;

                const tokenContract = new ethers.Contract(
                    tokenAddress,
                    tokenAbi,
                    signer
                );

                const approvalAmount = stakeAmount
                    ? safeParseUnits(stakeAmount, 18)
                    : ethers.MaxUint256;

                const approveTx = await tokenContract.approve(CONTRACT_ADDRESSES.STAKING, approvalAmount);

                toast.loading(t("validation.waitingApproval"), { id: "approval-tx" });
                await approveTx.wait();
                toast.success(t("validation.approvalSuccessful"), { id: "approval-tx" });
                setApprovalStatus('success');

                setTimeout(async () => {
                    setShowApproveModal(false);
                    setApprovalStatus(null);
                    setApprovalError(null);
                    setShowStakeModal(true);
                }, 2000);
            }
        } catch (error) {
            console.error("Approve stake error:", error);
            const errorMessage =
                error?.reason ||
                error?.shortMessage ||
                error?.message ||
                t("validation.approvalFailed");
            setApprovalError(errorMessage);
            setApprovalStatus("error");
            toast.error(errorMessage);
        } finally {
            setIsApproving(false);
        }
    }, [account, getWalletSigner, stakeAmount, selectedToken, safeParseUnits, t]);

    // Handle unstake
    const handleUnstake = async (stakeIndex) => {
        if (!account) {
            toast.error(t("validation.connectWalletFirst"));
            return;
        }
        setIsLoadingUnstake(true);
        try {
            const signer = await getWalletSigner();

            // Create staking contract instance
            const stakingContract = new ethers.Contract(
                CONTRACT_ADDRESSES.STAKING,
                CONTRACT_ABIS.STAKING,
                signer
            );

            // Call unstake method
            const unstakeTx = await stakingContract.unstake(
                parseInt(stakeIndex)
            );
            await unstakeTx.wait();

            toast.success(
                <div>
                    <p>{t("staking.successUnstaked")}</p>
                    <p className="text-xs mt-1">
                        TX:{" "}
                        <a
                            href={`https://bscscan.com/tx/${unstakeTx.hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700 underline"
                        >
                            {unstakeTx.hash.slice(0, 10)}...
                            {unstakeTx.hash.slice(-8)}
                        </a>
                    </p>
                </div>
            );

            // Refresh stakes
            // loadUserStakesFromContract();
        } catch (error) {
            console.error("Unstake error:", error);
            if (error.code === 4001) {
                toast.error(t("validation.transactionRejected"));
            } else {
                toast.error(t("validation.failedToStakeTokens"));
            }
        } finally {
            setIsLoadingUnstake(false);
        }
    };

    // Load user stakes from contract
    const loadUserStakesFromContract = useCallback(async () => {
        if (!account || !isConnected) {
            setIsLoadingHistory(false);
            return;
        }

        // Prevent multiple simultaneous calls
        if (isLoadingRef.current) {
            return;
        }

        isLoadingRef.current = true;
        setIsLoadingHistory(true);
        setLoadingProgress(t("staking.connectContractToLoad"));

        try {
            // Add timeout protection
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error("Loading timeout")), 60000)
            );

            const contractStakes = await Promise.race([
                getUserStakes(),
                timeoutPromise,
            ]);

            // Handle proxy contract response
            let stakesArray = [];
            if (contractStakes) {
                if (contractStakes.length !== undefined) {
                    stakesArray = Array.from(contractStakes);
                } else if (contractStakes.toArray) {
                    stakesArray = contractStakes.toArray();
                } else if (contractStakes.map) {
                    stakesArray = contractStakes;
                } else {
                    stakesArray = Object.values(contractStakes);
                }
            }

            setUserStakes(stakesArray);
            setLoadingProgress(
                `Loaded ${stakesArray.length} stakes successfully`
            );

            // Calculate total staked amount
            const total = stakesArray.reduce((sum, stake) => {
                if (stake.status === false) {
                    // Convert BigInt amount to readable format (wei to tokens)
                    const amountInTokens = parseFloat(
                        ethers.formatUnits(stake.amount.toString(), 18)
                    );
                    return sum + amountInTokens;
                }
                return sum;
            }, 0);

            setTotalStaked(total);

            // 3x max cap progress: earned / (totalStaked * 3) * 100. Pending vs claimable by period.
            const now = Date.now();
            let totalEarned = 0;
            let pending = 0;
            let claimable = 0;
            stakesArray.forEach((stake) => {
                const rewardVal = parseFloat(
                    ethers.formatUnits(String(stake.reward || "0"), 18)
                );
                totalEarned += rewardVal;
                if (stake.status === true) return;
                const stakeTime = Number(stake.stakeTime) * 1000;
                const opt = getStakeDaysOptions().find(
                    (o) => o.index === Number(stake.stakeIndex)
                );
                if (!opt) {
                    pending += rewardVal;
                    return;
                }
                const periodMs = opt.days * 24 * 60 * 60 * 1000;
                if (now - stakeTime >= periodMs) claimable += rewardVal;
                else pending += rewardVal;
            });
            setRewardCapProgress(
                total > 0
                    ? Math.min(100, (totalEarned / (total * 3)) * 100)
                    : 0
            );
            setTotalEarnedRewards(totalEarned);
            const fmt = (n) =>
                n.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                });
            setPendingRewards(`${fmt(pending)} USDT`);
            setClaimableRewards(`${fmt(claimable)} USDT`);

            // Convert contract stakes to history format
            const contractHistory = stakesArray.map((stake, index) => ({
                id: `contract_${stake.index || index}`,
                amount: parseFloat(
                    ethers.formatUnits(stake.amount.toString(), 18)
                ),
                ticketId: stake.stakeIndex?.toString(),
                txHash: `contract_${stake.index || index}`,
                timestamp: new Date(
                    Number(stake.stakeTime) * 1000
                ).toISOString(),
                status: stake.status ? "active" : "unstaked",
                source: "contract",
                stakeIndex: stake.index,
                stakeTime: stake.stakeTime,
                type: "stake",
            }));

            // Save contract stakes to localStorage
            localStorage.setItem(
                "contractStakesHistory",
                JSON.stringify(contractHistory)
            );

            // Merge with existing stake history
            const existingHistory = JSON.parse(
                localStorage.getItem("stakeHistory") || "[]"
            );
            const mergedHistory = [...contractHistory, ...existingHistory];

            // Remove duplicates based on stake index
            const uniqueHistory = mergedHistory.filter(
                (item, index, self) =>
                    index ===
                    self.findIndex((t) => t.stakeIndex === item.stakeIndex)
            );

            localStorage.setItem("stakeHistory", JSON.stringify(uniqueHistory));
            setStakeHistory(uniqueHistory);
            console.log("Stake history loading completed successfully");
        } catch (error) {
            // console.error("Error loading user stakes from contract:", error);
            // // Show user-friendly error message
            // if (error.message === "Loading timeout") {
            //     toast.error("Loading stakes timed out. Please try again.");
            // } else {
            //     toast.error(
            //         "Failed to load stake history. Please refresh the page."
            //     );
            // }
            // Reset to empty state on error
            // setUserStakes([]);
            // setTotalStaked(0);
        } finally {
            isLoadingRef.current = false;
            setIsLoadingHistory(false);
            setLoadingProgress("");
        }
    }, [account, isConnected, getUserStakes]);

    // Generate a random uniqueId and ensure it does not exist on-chain
    const generateUniqueId = useCallback(async () => {
        // Helper: create a decimal string candidate based on timestamp + random
        const createCandidate = () => {
            const now = Date.now().toString(); // milliseconds
            const rand = Math.floor(Math.random() * 1_000_000)
                .toString()
                .padStart(6, "0");
            return now + rand; // decimal string, safe for JSON + PHP gmp_init
        };

        const checkExists = async (candidate) => {
            return executeWithFailover(async (provider) => {
                const referralContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.REFERRALS,
                    CONTRACT_ABIS.REFERRALS,
                    provider
                );
                const exists = await referralContract.uniqueIdExists(candidate);
                return Boolean(exists);
            });
        };

        const maxAttempts = 5;
        for (let i = 0; i < maxAttempts; i++) {
            const candidate = createCandidate();
            try {
                const exists = await checkExists(candidate);
                if (!exists) {
                    return candidate;
                }
            } catch (err) {
                console.error("Error checking uniqueIdExists:", err);
            }
        }

        throw new Error("Failed to generate a unique uniqueId");
    }, [executeWithFailover]);

    // Generate referral link
    const getReferralLink = () => {
        if (!account) return "";
        const baseUrl = window.location.origin;
        return `${baseUrl}/staking?ref=${account}`;
    };

    // Copy referral link to clipboard
    const copyReferralLink = () => {
        const link = getReferralLink();
        navigator.clipboard.writeText(link).then(
            () => {
                toast.success(
                    t("staking.referralLinkCopied") || "Referral link copied!"
                );
            },
            () => {
                toast.error(t("dashboard.failedReferralLinkCopied") || "Failed to copy referral link");
            }
        );
    };

    const getRanking = useCallback(async () => {
        if (!account || !isConnected) return;
        try {
            const ranking = await executeWithFailover(async (provider) => {
                const stakingContract = new ethers.Contract(
                    CONTRACT_ADDRESSES.STAKING,
                    CONTRACT_ABIS.STAKING,
                    provider
                );
                const ranking = await stakingContract.userActiveRank(account);
                return Number(ranking);
            });
            setRanking(ranking);
        } catch (error) {
            console.error("Error getting ranking:", error);
        }
    }, [account, isConnected, executeWithFailover]);

    useEffect(() => {
        getRanking();
    }, [getRanking]);

    // Update current time every minute for countdown
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 60000); // Update every minute

        return () => clearInterval(interval);
    }, []);

    // Store referral address from URL to localStorage (before wallet connection)
    useEffect(() => {
        const refAddress = searchParams.get("ref");

        if (refAddress && ethers.isAddress(refAddress)) {
            // Store referral address in localStorage for later use
            localStorage.setItem("pendingReferralAddress", refAddress);
        }
    }, [searchParams]);

    // Auto-fill referral address from localStorage when wallet connects
    useEffect(() => {
        // Only auto-fill if not bound and no referral address is set
        if (account && isConnected && !isReferralBound && !referralAddress) {
            const storedReferralAddress = localStorage.getItem(
                "pendingReferralAddress"
            );
            if (
                storedReferralAddress &&
                ethers.isAddress(storedReferralAddress) &&
                storedReferralAddress.toLowerCase() !== account.toLowerCase()
            ) {
                setReferralAddress(storedReferralAddress);
            }

            if (searchParams.get("ticketId")) {
                setTicketId(searchParams.get("ticketId"));
            }
        }
    }, [account, isConnected, isReferralBound, referralAddress, t]);

    // Check existing referral when account changes
    useEffect(() => {
        if (!account || !isConnected) {
            return undefined;
        }

        // Clear referral address field immediately when account changes
        setReferralAddress("");
        setIsReferralBound(false);
        hasCheckedReferral.current = false; // Reset check flag for new account

        // Then check for existing referral (only once per account change)
        const checkReferral = async () => {
            if (!hasCheckedReferral.current) {
                hasCheckedReferral.current = true;
                await checkExistingReferral();
            }
        };

        const referralCheckTimer = setTimeout(checkReferral, 100); // Small delay to ensure state is cleared first
        return () => clearTimeout(referralCheckTimer);
    }, [account, isConnected, checkExistingReferral]);

    // Clear data when wallet is disconnected
    useEffect(() => {
        if (!account || !isConnected) {
            setUsdtBalance("0");
            setAbnbUsdtBalance("0");
            setVtBalance("0");
            setUserStakes([]);
            setTotalStaked(0);
            setMaxStakeAmount("0");
            setPendingRewards("0.00 USDT");
            setClaimableRewards("0.00 USDT");
            setRewardCapProgress(0);
            setTotalEarnedRewards(0);
            setIsReferralBound(false);
            setReferralAddress("");
            setRanking(0);
        }
    }, [account, isConnected]);

    // Load data on component mount
    useEffect(() => {
        fetchUsdtBalance();
    }, [
        fetchUsdtBalance,
    ]);

    const claimRewards = useCallback(async () => {
        if (!account) {
            toast.error(t("validation.connectWalletFirst"));
            return;
        }
        setIsLoadingClaimRewards(true);
        try {
            const signer = await getWalletSigner();

            // Create staking contract instance
            const stakingContract = new ethers.Contract(
                CONTRACT_ADDRESSES.STAKING,
                CONTRACT_ABIS.STAKING,
                signer
            );

            // Call unstake method
            const claimRewardsTx = await stakingContract.claimRewards();
            await claimRewardsTx.wait();

            setShowClaimModal(false);
            setProcessClaimStep(2);
            setShowProcessClaimModal(true);

            toast.success(
                <div>
                    <p>{t("staking.successClaimRewards")}</p>
                    <p className="text-xs mt-1">
                        TX:{" "}
                        <a
                            href={`https://bscscan.com/tx/${claimRewardsTx.hash}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-500 hover:text-blue-700 underline"
                        >
                            {claimRewardsTx.hash.slice(0, 10)}...{claimRewardsTx.hash.slice(-8)}
                        </a>
                    </p>
                </div>
            );
        } catch (error) {
            console.error("claimRewards error:", error);
            if (error.code === 4001) {
                toast.error(t("validation.transactionRejected"));
            } else {
                toast.error(
                    error?.reason ||
                    error?.shortMessage ||
                    error?.message ||
                    t("staking.failedClaimRewards")
                );
            }
        } finally {
            setIsLoadingClaimRewards(false);
        }
    }, [account, t, getWalletSigner]);

    // const claimRewards = useCallback(async () => {
    //     if (!account) {
    //         toast.error(t("validation.connectWalletFirst"));
    //         return;
    //     }
    //     setShowClaimModal(false);
    //     setIsLoadingClaimRewards(true);
    //     try {
    //         setProcessClaimStep(1);
    //         const provider = new ethers.BrowserProvider(window.ethereum);
    //         const signer = await provider.getSigner();

    //         // Create staking contract instance
    //         const stakingContract = new ethers.Contract(
    //             CONTRACT_ADDRESSES.STAKING,
    //             CONTRACT_ABIS.STAKING,
    //             signer
    //         );

    //         // Call unstake methods
    //         const claimRewardsTx = await stakingContract.claimRewards();
    //         await claimRewardsTx.wait();

    //         setShowProcessClaimModal(true);
    //         setTimeout(() => {
    //             setProcessClaimStep(2);
    //         }, 600);

    //         toast.success(
    //             <div>
    //                 <p>{t("staking.successClaimRewards")}</p>
    //                 <p className="text-xs mt-1">
    //                     TX:{" "}
    //                     <a
    //                         href={`https://bscscan.com/tx/${claimRewardsTx.hash}`}
    //                         target="_blank"
    //                         rel="noopener noreferrer"
    //                         className="text-blue-500 hover:text-blue-700 underline"
    //                     >
    //                         {claimRewardsTx.hash.slice(0, 10)}...{claimRewardsTx.hash.slice(-8)}
    //                     </a>
    //                 </p>
    //             </div>
    //         );
    //     } catch (error) {
    //         console.error("claimRewards error:", error);
    //         if (error.code === 4001) {
    //             toast.error(t("validation.transactionRejected"));
    //         } else {
    //             toast.error(
    //                 error?.reason ||
    //                 error?.shortMessage ||
    //                 error?.message ||
    //                 t("staking.failedClaimRewards")
    //             );
    //         }
    //     } finally {
    //         setIsLoadingClaimRewards(false);
    //     }
    // }, [account, t]);

    const generateAtokenWithdrawalUniqueId = useCallback(() => {
        const now = Date.now().toString();
        const rand = Math.floor(Math.random() * 1_000_000).toString().padStart(6, "0");
        return `${now}${rand}`;
    }, []);

    const handleRestake = useCallback(() => {
        if (!account || !isConnected) {
            toast.error(t("validation.connectWalletFirst") || "Connect wallet first");
            return;
        }
        const amount = Number(atokenBalance || 0);
        if (!Number.isFinite(amount) || amount < MIN_YIELD_CLAIM_AMOUNT) {
            toast.error(t("validation.minimumClaimAmount", { minClaimAmount: MIN_YIELD_CLAIM_AMOUNT }) || `Minimum restake amount is ${MIN_YIELD_CLAIM_AMOUNT}`);
            return;
        }
        setShowRestakeModal(true);
    }, [account, atokenBalance, isConnected, t, MIN_YIELD_CLAIM_AMOUNT]);

    const confirmRestake = useCallback(async () => {
        if (!account || !isConnected) {
            toast.error(t("validation.connectWalletFirst") || "Connect wallet first");
            return;
        }
        const amount = Number(atokenBalance || 0);
        if (!Number.isFinite(amount) || amount < MIN_YIELD_CLAIM_AMOUNT) {
            toast.error(t("validation.minimumClaimAmount", { minClaimAmount: MIN_YIELD_CLAIM_AMOUNT }) || `Minimum restake amount is ${MIN_YIELD_CLAIM_AMOUNT}`);
            return;
        }
        setShowRestakeModal(false);
        setIsRestaking(true);
        try {
            const response = await fetch(`${API_BASE}/publicApi/restakeAtoken`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    address: account,
                    amount: String(amount),
                    ref_address: referralAddress || "",
                    uniqueId: `restake-${Date.now()}-${Math.floor(Math.random() * 1000000)}`,
                }),
            });
            const result = await response.json().catch(() => ({}));
            if (!response.ok || !result.success) {
                throw new Error(result.error || result.message || "Restake failed");
            }
            setIsRestaking(false);
            setShowRestakeSuccessModal(true);
            void fetchUsdtBalance().catch((refreshError) => console.warn("Balance refresh after restake failed:", refreshError));
            void fetchYieldStakeSummary().catch((refreshError) => console.warn("Yield summary refresh after restake failed:", refreshError));
        } catch (error) {
            console.error("aBnBUsdt restake error:", error);
            toast.error(error?.message || "Restake failed");
        } finally {
            setIsRestaking(false);
        }
    }, [account, atokenBalance, fetchUsdtBalance, fetchYieldStakeSummary, isConnected, referralAddress, t, MIN_YIELD_CLAIM_AMOUNT]);

    const openAtokenWithdrawalModal = useCallback(() => {
        if (!account || !isConnected) {
            toast.error(t("validation.connectWalletFirst") || t("swap.connectWalletFirst") || "Connect wallet first");
            return;
        }
        const amount = Number(atokenBalance || 0);
        if (!Number.isFinite(amount) || amount <= 0) {
            toast.error(t("yield.noAtokenBalance") || "No aBnBUsdt balance to claim");
            return;
        }
        if (amount < MIN_YIELD_CLAIM_AMOUNT) {
            toast.error(t("validation.minimumClaimAmount", { minClaimAmount: MIN_YIELD_CLAIM_AMOUNT }) || `Minimum claim amount is ${MIN_YIELD_CLAIM_AMOUNT}`);
            return;
        }
        setShowAtokenWithdrawalModal(true);
    }, [account, isConnected, atokenBalance, t, MIN_YIELD_CLAIM_AMOUNT]);

    const confirmAtokenWithdrawal = useCallback(async () => {
        const amount = Number(atokenBalance || 0);
        if (!Number.isFinite(amount) || amount <= 0) {
            toast.error(t("yield.noAtokenBalance") || "No aBnBUsdt balance to claim");
            return;
        }
        if (amount < MIN_YIELD_CLAIM_AMOUNT) {
            toast.error(t("validation.minimumClaimAmount", { minClaimAmount: MIN_YIELD_CLAIM_AMOUNT }) || `Minimum claim amount is ${MIN_YIELD_CLAIM_AMOUNT}`);
            return;
        }
        setIsWithdrawingAtoken(true);
        try {
            const res = await fetch(`${API_BASE}/publicApi/withdrawalToken`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    walletAddress: account,
                    amount: String(amount),
                    uniqueId: generateAtokenWithdrawalUniqueId(),
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data.success) {
                throw new Error(data.error || data.message || t("yield.withdrawalFailed") || "Withdrawal failed");
            }
            toast.success(t("yield.withdrawalSubmitted") || "Withdrawal request submitted");
            setShowAtokenWithdrawalModal(false);
            fetchUsdtBalance();
        } catch (error) {
            console.error("aBnBUsdt withdrawal error:", error);
            toast.error(error?.message || t("yield.withdrawalFailed") || "Withdrawal failed");
        } finally {
            setIsWithdrawingAtoken(false);
        }
    }, [account, atokenBalance, fetchUsdtBalance, generateAtokenWithdrawalUniqueId, t, MIN_YIELD_CLAIM_AMOUNT]);

    const handleCoseProcessStakeModal = () => {
        setShowProcessStakeModal(false);
        setStakeAmount('');
    };

    const getStakeTime = useCallback(async () => {
        if (!account || !isConnected || !isCorrectNetwork) {
            return;
        }

        try {
            const provider = getProvider();

            // Create staking contract instance
            const stakingContract = new ethers.Contract(
                CONTRACT_ADDRESSES.STAKING,
                CONTRACT_ABIS.STAKING,
                provider
            );

            // Call unstake method
            const getUserStakeRecord = await stakingContract.userStakeRecord(account);
            console.log("getUserStakeRecord", getUserStakeRecord);

        } catch (error) {

        }

    }, [account, isConnected, isCorrectNetwork]);

    useEffect(() => {
        getStakeTime();
    }, [getStakeTime]);

    return (
        <div className="relative overflow-x-hidden min-h-[calc(100vh-200px)]">
            {/* Background gradient - darker purple at top/bottom, lighter in middle */}
            {/*<div className="absolute inset-0 bg-gradient-to-b from-[#0a0610] via-[#241630] to-[#0a0610] -z-10"></div>*/}

            {/* Subtle geometric pattern overlay */}
            {/*<div
                className="absolute inset-0 opacity-[0.03] pointer-events-none -z-10"
                style={{
                    backgroundImage: `radial-gradient(circle at 2px 2px, #9770DD 1px, transparent 0)`,
                    backgroundSize: '40px 40px'
                }}
            ></div>*/}

            {/* Additional pattern overlay for variety */}
            {/*<div
                className="absolute inset-0 opacity-[0.02] pointer-events-none -z-10"
                style={{
                    backgroundImage: `linear-gradient(45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%), linear-gradient(-45deg, transparent 48%, #9770DD 49%, #9770DD 51%, transparent 52%)`,
                    backgroundSize: '60px 60px'
                }}
            ></div> */}

            {/* Wavy pattern overlay */}
            {/*<div
                className="absolute inset-0 opacity-[0.05] pointer-events-none -z-10"
                style={{
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%239770DD' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E")`,
                    backgroundSize: '200px 200px'
                }}
            ></div>*/}

            {/* ── Staking Hero Section ── */}
            <div className="relative z-10 max-w-[550px] mx-auto pt-4 pb-2 space-y-4">
                <div className="flex items-center gap-3 mb-4">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="flex-shrink-0 size-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-colors"
                        aria-label={t("common.back")}
                    >
                        <span className="material-symbols-outlined text-2xl">arrow_back</span>
                    </button>
                    <div className="flex-1 min-w-0">
                        <h1 className="text-xl font-semibold text-white truncate">{isYieldPage ? (t("yield.title") || "Yield") : (t("staking.title") || "Staking NFT")}</h1>
                        <p className="text-sm text-gray-500 truncate">{isYieldPage ? (t("yield.subtitle") || "USD staking") : (t("staking.pageSubtitle") || "Track your earnings and manage your claim")}</p>
                    </div>
                    <span
                        className="flex-shrink-0 size-14 rounded-full flex items-center justify-center text-[#DFD2FE] hover:bg-[#9770DD]/20 transition-colors opacity-70"
                        aria-label={isYieldPage ? (t("yieldHistory.title") || "Yield History") : (t("stakingHistory.title") || "Staking history")}
                        role="button"
                        onClick={() => navigate(isYieldPage ? "/yield-history" : "/staking-history")}
                    >
                        <img src={IconHistoryStaked} alt="History" className="size-8 cursor-pointer" />
                    </span>
                </div>

                {!isYieldPage && (
                    <button
                        type="button"
                        onClick={() => navigate("/yield")}
                        className="w-full rounded-2xl border border-[#9770DD]/50 bg-gradient-to-r from-[#3b1f66] via-[#5B47CD] to-[#8b5cf6] p-4 text-left shadow-lg shadow-[#5B47CD]/20 hover:scale-[1.01] active:scale-[0.99] transition-all"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div>
                                <p className="text-white text-lg font-extrabold leading-tight">{t("yield.buttonTitle") || "Yield"}</p>
                                <p className="text-[#DFD2FE] text-xs font-semibold mt-1">{t("yield.buttonSubtitle") || "VT + GRAM Staking"}</p>
                                <p className="text-[#FDE68A] text-xs font-extrabold mt-1 tracking-wide">{t("yield.buttonApy") || "APY 60% - 240%"}</p>
                            </div>
                            <div className="flex items-center gap-2 text-white text-sm font-bold">
                                <span>{t("yield.buttonCta") || "Go Yield"}</span>
                                <span className="material-symbols-outlined text-xl">arrow_forward</span>
                            </div>
                        </div>
                    </button>
                )}

                {/* Total Staked Card */}
                <div
                    className="rounded-2xl border border-[#2a1a42] overflow-hidden flex items-center justify-between p-5"
                >
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <span
                                className="size-8 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ background: 'rgba(124,58,237,0.25)' }}
                            >
                                <FiLock className="size-4 text-[#a78bfa]" />
                            </span>
                            <span className="text-[#7c6a9a] text-[11px] font-semibold uppercase tracking-widest">
                                {t("staking.totalStakedLabel") || "Total Staked"}
                            </span>
                        </div>
                        <p className="text-white font-extrabold text-2xl leading-none">
                            {isYieldPage
                                ? (isLoadingYieldSummary ? "..." : Number(yieldStakeSummary.totalStake || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
                                : (userROIAndCapLive?.stakeAmount != "0" || userROIAndCapLive?.stakeAmount != null ? userROIAndCapLive?.stakeAmount : "0")}
                            <span className="text-[#7c6a9a] text-base font-semibold ml-2">USDT</span>
                        </p>
                    </div>
                    <img
                        src={Img3dTotalStaked}
                        alt="staked"
                        className="size-24 object-contain flex-shrink-0"
                        style={{ filter: 'drop-shadow(0 0 18px rgba(124,58,237,0.6))' }}
                    />
                </div>

                {isYieldPage && (
                    <div className="rounded-2xl border border-[#2a1a42] bg-[#0a0610]/70 p-4 shadow-lg shadow-[#5B47CD]/10">
                        <div className="flex items-center justify-between gap-3">
                            <div className="flex items-center gap-3 min-w-0">
                                <span className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#5b47cd] border border-[#7c3aed]/60" style={{ background: 'rgb(200,168,255)' }}>aB</span>
                                <div className="min-w-0">
                                    <p className="text-[#7c6a9a] text-[10px] font-semibold uppercase tracking-wider truncate">{t("yield.aBnbUsdtBalance") || "aBnBUsdt Balance"}</p>
                                    <p className="text-white font-bold text-base truncate">
                                        {isLoadingBalance ? "..." : formatBalanceForDisplay(atokenBalance, 6)}
                                        <span className="text-[#7c6a9a] font-semibold ml-1 text-xs">aBnBUsdt</span>
                                    </p>
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={handleRestake}
                                disabled={isRestaking || Number(atokenBalance || 0) < MIN_YIELD_CLAIM_AMOUNT}
                                className="flex-shrink-0 rounded-xl border border-[#9770DD]/60 bg-[rgba(151,112,221,0.18)] px-4 py-2 text-xs font-bold text-[#DFD2FE] hover:bg-[rgba(151,112,221,0.32)] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isRestaking ? "Restaking..." : "Restake"}
                            </button>
                            <button
                                type="button"
                                onClick={claimEnabled ? openAtokenWithdrawalModal : undefined}
                                disabled={!claimEnabled || isLoadingClaimStatus || isWithdrawingAtoken || Number(atokenBalance || 0) < MIN_YIELD_CLAIM_AMOUNT}
                                title={Number(atokenBalance || 0) > 0 && Number(atokenBalance || 0) < MIN_YIELD_CLAIM_AMOUNT ? (t("validation.minimumClaimAmount", { minClaimAmount: MIN_YIELD_CLAIM_AMOUNT }) || `Minimum claim amount is ${MIN_YIELD_CLAIM_AMOUNT}`) : undefined}
                                className="flex-shrink-0 rounded-xl border border-[#7c3aed]/60 bg-[rgba(124,58,237,0.18)] px-4 py-2 text-xs font-bold text-[#DFD2FE] hover:bg-[rgba(124,58,237,0.32)] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {t("yield.claim") || "Claim"}
                            </button>
                        </div>
                        {Number(atokenBalance || 0) > 0 && Number(atokenBalance || 0) < MIN_YIELD_CLAIM_AMOUNT && (
                            <div className="mt-2 text-[#ff6b6b] text-xs font-semibold leading-snug">
                                {t("validation.minimumClaimAmount", { minClaimAmount: MIN_YIELD_CLAIM_AMOUNT }) || `Minimum claim amount is ${MIN_YIELD_CLAIM_AMOUNT}`}
                            </div>
                        )}
                    </div>
                )}

                {isYieldPage && (
                    <div className="grid grid-cols-2 gap-3 rounded-2xl border border-[#2a1a42] bg-[#0a0610]/70 p-4 shadow-lg shadow-[#5B47CD]/10">
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white border border-[#9770DD]/60 bg-gradient-to-br from-[#9770DD] to-[#ff6b6b]">VT</span>
                            <div className="min-w-0">
                                <p className="text-[#7c6a9a] text-[10px] font-semibold uppercase tracking-wider truncate">VT Balance</p>
                                <p className="text-white font-bold text-sm sm:text-base truncate">
                                    {isLoadingBalance ? "..." : formatBalanceForDisplay(vtBalance, 6)}
                                    <span className="text-[#7c6a9a] font-semibold ml-1 text-xs">VT</span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => navigate("/deposit-vt")}
                                    className="mt-1 inline-flex items-center gap-1 rounded-md border border-[#7c3aed]/60 bg-[rgba(124,58,237,0.18)] px-2 py-0.5 text-[10px] font-bold text-[#DFD2FE] hover:bg-[rgba(124,58,237,0.32)] active:scale-[0.98] transition-all"
                                >
                                    Top Up
                                    <span className="material-symbols-outlined text-[12px] leading-none">arrow_forward</span>
                                </button>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 min-w-0">
                            <span className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white border border-[#F59E0B]/60 bg-gradient-to-br from-[#F59E0B] to-[#FDE68A]">GR</span>
                            <div className="min-w-0">
                                <p className="text-[#7c6a9a] text-[10px] font-semibold uppercase tracking-wider truncate">GRAM Balance</p>
                                <p className="text-white font-bold text-sm sm:text-base truncate">
                                    {isLoadingBalance ? "..." : formatBalanceForDisplay(gramBalance, 6)}
                                    <span className="text-[#7c6a9a] font-semibold ml-1 text-xs">GRAM</span>
                                </p>
                                <button
                                    type="button"
                                    onClick={() => navigate("/deposit")}
                                    className="mt-1 inline-flex items-center gap-1 rounded-md border border-[#7c3aed]/60 bg-[rgba(124,58,237,0.18)] px-2 py-0.5 text-[10px] font-bold text-[#DFD2FE] hover:bg-[rgba(124,58,237,0.32)] active:scale-[0.98] transition-all"
                                >
                                    {t("yield.buyGram") || "Buy Gram"}
                                    <span className="material-symbols-outlined text-[12px] leading-none">arrow_forward</span>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                {/* ── Stake Token Section ── */}
                <div className="relative z-10 max-w-[550px] mx-auto pt-6 pb-4 space-y-4">

                    {/* Title */}
                    <h2 className="text-white font-semibold text-xl">{isYieldPage ? (t("yield.stakeTokensTitle") || "USD Yield") : (t("staking.stakeTokensTitle") || "Stake Tokens")}</h2>

                    {/* Balance Card */}
                    <div
                        className="rounded-2xl border border-[#2a1a42] p-5"
                    >
                        {!isYieldPage && (
                            <>
                                <p className="text-[#7c6a9a] text-xs font-medium uppercase tracking-wider mb-2">{t("swap.balance")}</p>

                                {/* Large amount */}
                                <div className="flex items-baseline justify-between mb-5">
                                    <div className="flex items-baseline gap-1">
                                        <span className="text-white font-extrabold text-4xl leading-none">
                                            {selectedToken === "USDT"
                                                ? usdtBalance || 0
                                                : selectedToken === "aBnBusdt"
                                                    ? abnbUsdtBalance || 0
                                                    : formatBalanceForDisplay(vtGramStakeableBalance)}
                                        </span>
                                    </div>
                                    <span className="text-[#7c6a9a] text-base font-semibold">{selectedToken === "VT Balance" ? "USD" : selectedToken}</span>
                                </div>
                            </>
                        )}

                        {/* Three balance pills */}
                        <div className={`grid ${isYieldPage ? "grid-cols-1" : "grid-cols-2"} gap-1.5 sm:gap-2`}>
                            {!isYieldPage && (
                                <>
                                    <div
                                        className={`flex items-center gap-1.5 rounded-xl px-2 sm:px-3 py-2.5 border cursor-pointer transition-all ${selectedToken === "USDT" ? "border-[#7c3aed]/60 bg-[rgba(124,58,237,0.15)]" : "border-[#2a1a42] bg-[rgba(124,58,237,0.05)]"}`}
                                        onClick={() => setSelectedToken("USDT")}
                                    >
                                        <span className="w-6 sm:w-8 h-6 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold text-white"><img src={IconTokenUsdt} alt="Token USDT" className="w-full h-full object-contain" /></span>
                                        <div className="min-w-0">
                                            <p className="text-[#7c6a9a] text-[9px] sm:text-[10px] truncate">{t("staking.balance") || "USDT"}</p>
                                            <p className="text-[#5b47cd] font-bold text-[11px] sm:text-xs md:text-sm leading-none truncate">
                                                {usdtBalance}
                                                <span className="text-[#7c6a9a] font-normal ml-0.5 text-[8px] sm:text-[9px] md:text-[10px]">USDT</span>
                                            </p>
                                        </div>
                                    </div>
                                    <div
                                        className={`flex items-center gap-1.5 rounded-xl px-2 sm:px-3 py-2.5 border cursor-pointer transition-all ${selectedToken === "aBnBusdt" ? "border-[#7c3aed]/60 bg-[rgba(124,58,237,0.15)]" : "border-[#2a1a42] bg-[rgba(124,58,237,0.05)]"}`}
                                        onClick={() => setSelectedToken("aBnBusdt")}
                                    >
                                        <span className="w-6 sm:w-8 h-6 sm:h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-[#5b47cd] border border-[#7c3aed]/60" style={{ background: 'rgb(200,168,255)' }}>aB</span>
                                        <div className="min-w-0">
                                            <p className="text-[#7c6a9a] text-[9px] sm:text-[10px] truncate">{t("staking.abnbbalance") || "aBnBusdt"}</p>
                                            <p className="text-[#5b47cd] font-bold text-[11px] sm:text-xs md:text-sm leading-none truncate">
                                                {abnbUsdtBalance || 0}
                                                <span className="text-[#7c6a9a] font-normal ml-0.5 text-[8px] sm:text-[9px] md:text-[10px]">aBnB</span>
                                            </p>
                                        </div>
                                    </div>
                                </>
                            )}
                            {isYieldPage && (
                                <div className="grid grid-cols-[1fr_auto_1fr] items-stretch gap-2">
                                    <div className="flex items-center gap-2 rounded-xl px-3 py-3 border border-[#7c3aed]/60 bg-[rgba(124,58,237,0.15)] transition-all min-w-0">
                                        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white border border-[#9770DD]/60 bg-gradient-to-br from-[#9770DD] to-[#ff6b6b]">VT</span>
                                        <div className="min-w-0">
                                            <p className="text-[#7c6a9a] text-[9px] sm:text-[10px] truncate">VT 50%</p>
                                            <p className="text-[#5b47cd] font-bold text-xs sm:text-sm leading-none truncate">
                                                {yieldHalfStakeAmount}
                                                <span className="text-[#7c6a9a] font-normal ml-1 text-[9px] sm:text-[10px]">VT</span>
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-center text-[#7c3aed] text-lg font-bold">+</div>
                                    <div className="flex items-center gap-2 rounded-xl px-3 py-3 border border-[#7c3aed]/60 bg-[rgba(124,58,237,0.15)] transition-all min-w-0">
                                        <span className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white border border-[#F59E0B]/60 bg-gradient-to-br from-[#F59E0B] to-[#FDE68A]">GR</span>
                                        <div className="min-w-0">
                                            <p className="text-[#7c6a9a] text-[9px] sm:text-[10px] truncate">Gram 50%</p>
                                            <p className="text-[#5b47cd] font-bold text-xs sm:text-sm leading-none truncate">
                                                {yieldHalfStakeAmount}
                                                <span className="text-[#7c6a9a] font-normal ml-1 text-[9px] sm:text-[10px]">GRAM</span>
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Info Row: Min Stake / Max Stake / Network */}
                    {selectedToken == 'USDT' && (
                        <div
                            className="rounded-2xl border border-[#2a1a42] p-4 grid grid-cols-2 divide-x divide-[#2a1a42]"
                        >
                            {[
                                { icon: "📉", label: t("staking.min") || "Min", value: "100 USDT" },
                                { icon: "📦", label: t("staking.max") || "Max", value: "10,000 USDT" },
                            ].map((item, i) => (
                                <div key={i} className="flex flex-col items-center gap-1.5 px-2">
                                    <span className="text-lg">{item.icon}</span>
                                    <p className="text-[#7c6a9a] text-[10px] uppercase tracking-wider">{item.label}</p>
                                    <p className="text-white text-xs font-semibold text-center">{item.value}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Enter Amount */}
                    <div
                        className="rounded-2xl border border-[#2a1a42] p-5 space-y-4"
                    >
                        <div className="flex items-center gap-2">
                            <span className="size-7 rounded-lg flex items-center justify-center">
                                <img src={ImgTotalStaked} alt="amount" className="size-4 object-contain" />
                            </span>
                            <span className="text-white font-semibold text-base">{t("staking.enterAmount") || "Enter Amount"}</span>
                        </div>

                        {/* Input + Token selector */}
                        <div
                            className="flex items-center rounded-xl px-4 h-14 border border-[#2a1a42] focus-within:border-[#7c3aed]/60 transition-colors overflow-hidden"
                            style={{ background: 'rgba(124,58,237,0.06)' }}
                        >
                            <input
                                type="number"
                                min="0"
                                value={stakeAmount}
                                onChange={(e) => setStakeAmount(e.target.value)}
                                placeholder={selectedToken === "USDT" ? "0.00 USDT" : selectedToken === "aBnBusdt" ? "0.00 aBnBusdt" : "0.00 USD"}
                                className="flex-1 min-w-0 bg-transparent text-white font-semibold text-base outline-none placeholder-[#4a3a6a]"
                            />
                            <div
                                className={`flex-shrink-0 flex items-center gap-1.5 ml-2 px-2 py-1.5 rounded-lg border border-[#2a1a42] transition-colors ${isYieldPage ? "cursor-default" : "cursor-pointer hover:border-[#7c3aed]/50"}`}
                                style={{ background: 'rgba(124,58,237,0.12)' }}
                                onClick={() => {
                                    if (isYieldPage) return;
                                    if (selectedToken === "USDT") {
                                        setSelectedToken("aBnBusdt");
                                    } else {
                                        setSelectedToken("USDT");
                                    }
                                }}
                            >
                                <span className="size-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0" style={{
                                    background: selectedToken === "USDT"
                                        ? '#26a17b'
                                        : selectedToken === "aBnBusdt"
                                            ? 'rgba(124,58,237,0.5)'
                                            : 'linear-gradient(135deg, #9770DD, #ff6b6b)'
                                }}>
                                    {selectedToken === "USDT" ? "₮" : selectedToken === "aBnBusdt" ? "aB" : "VT"}
                                </span>
                                <span className="text-white text-xs font-semibold whitespace-nowrap">{selectedToken === "VT Balance" ? "USD" : selectedToken}</span>
                                {!isYieldPage && <svg className="size-3.5 text-[#7c6a9a] flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>}
                            </div>
                        </div>

                        {isYieldStakeBelowMinimum && (
                            <div className="text-[#ff6b6b] text-xs font-semibold leading-snug">
                                {t("validation.minimumStakeAmount", { minStakeAmount: MIN_YIELD_STAKE_AMOUNT }) || `Minimum staking amount is ${MIN_YIELD_STAKE_AMOUNT}`}
                            </div>
                        )}

                        {/* Percentage quick-select */}
                        <div className="flex items-center gap-2">
                            <span className="text-[#7c6a9a] text-sm font-semibold min-w-[36px]">
                                {stakeAmount && parseFloat(selectedToken === "USDT" ? usdtBalance : selectedToken === "aBnBusdt" ? abnbUsdtBalance : vtGramStakeableBalance) > 0
                                    ? `${Math.round((parseFloat(stakeAmount) / parseFloat(selectedToken === "USDT" ? usdtBalance : selectedToken === "aBnBusdt" ? abnbUsdtBalance : vtGramStakeableBalance)) * 100)}%`
                                    : "0%"}
                            </span>
                            {[
                                { label: "25%", pct: 0.25 },
                                { label: "50%", pct: 0.5 },
                                { label: "70%", pct: 0.7 },
                                { label: "Max", pct: 1.0 },
                            ].map(({ label, pct }) => (
                                <button
                                    key={label}
                                    type="button"
                                    onClick={() => {
                                        const bal = parseFloat(selectedToken === "USDT" ? usdtBalance : selectedToken === "aBnBusdt" ? abnbUsdtBalance : vtGramStakeableBalance);
                                        setStakeAmount((bal * pct).toFixed(2));
                                    }}
                                    className="flex-1 h-10 rounded-xl text-[#a78bfa] text-xs font-semibold border border-[#7c3aed]/40 hover:bg-[rgba(124,58,237,0.2)] transition-colors"
                                    style={{ background: 'rgba(124,58,237,0.08)' }}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Referral Address */}
                    {!isReferralBound && (
                        <input
                            type="text"
                            value={referralAddress}
                            onChange={(e) => setReferralAddress(e.target.value)}
                            placeholder="Enter Referral Address"
                            className="w-full h-12 rounded-xl px-4 bg-transparent border border-[#2a1a42] text-white text-sm outline-none placeholder-[#4a3a6a] focus:border-[#7c3aed]/60 transition-colors"
                        />
                    )}

                    {/* Stake Button */}
                    <button
                        type="button"
                        onClick={checkAllowance}
                        disabled={isLoadingStake || !account || !stakeAmount || isYieldStakeButtonDisabled}
                        className="w-full h-14 rounded-full text-white font-semibold text-base transition-all duration-300 hover:opacity-90 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
                        style={{ background: '#5B47CD' }}
                    >
                        {isLoadingStake || isApproving ? (
                            <span className="flex items-center justify-center gap-2">
                                <FiRotateCcw className="animate-spin size-4" />
                                {isApproving ? t("common.approving") : t("staking.processing")}
                            </span>
                        ) : t("staking.stakeNow")}
                    </button>
                </div>





                {/* Your Rewards */}
                <div className="p-5">
                    {/* Header row */}
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="text-white font-semibold text-xl">{t("staking.yourRewards") || "Your Rewards"}</h3>
                        <span
                            className="px-3 py-1 rounded-lg text-xs font-bold text-[#7c6a9a] tracking-wider"
                            style={{ background: '#fff', border: '1px solid #7c6a9a' }}
                        >
                            {t("staking.live") || "LIVE"}
                        </span>
                    </div>

                    {isYieldPage ? (
                        <div className="mt-4 space-y-3">
                            {isLoadingYieldBonusList && (
                                <div className="rounded-xl p-5 border border-[#2a1a42] flex items-center justify-center gap-3 text-[#938F99]" style={{ background: 'rgba(124,58,237,0.08)' }}>
                                    <div className="animate-spin rounded-full size-5 border-b-2 border-[#a855f7]"></div>
                                    <span>{t("common.loading") || "Loading..."}</span>
                                </div>
                            )}

                            {!isLoadingYieldBonusList && yieldBonusList.length === 0 && (
                                <div className="rounded-xl p-6 border border-[#2a1a42] text-center" style={{ background: 'rgba(124,58,237,0.08)' }}>
                                    <p className="text-white font-semibold">No Yield Bonus Yet</p>
                                    <p className="text-[#938F99] text-sm mt-1">Your yield bonus list will appear here.</p>
                                </div>
                            )}

                            {!isLoadingYieldBonusList && yieldBonusList.map((bonus) => (
                                <div
                                    key={bonus.mc_id || `${bonus.mo_id}-${bonus.mc_odate}-${bonus.mc_amount}`}
                                    className="rounded-xl p-4 border border-[#2a1a42] transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.35)] hover:border-[#7c3aed]/60"
                                    style={{ background: 'rgba(124,58,237,0.08)' }}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <span className="size-9 rounded-full border border-[#2a1a42] flex items-center justify-center flex-shrink-0">
                                                <img src={ImgClaimable} alt="yield bonus" className="size-5 object-contain" />
                                            </span>
                                            <div className="min-w-0">
                                                <p className="text-white font-bold text-lg leading-none">
                                                    {formatYieldBonusAmount(bonus.mc_amount)}
                                                    <span className="text-[#7c6a9a] text-xs font-semibold ml-2">USDT</span>
                                                </p>
                                                <div className="flex flex-wrap items-center gap-2 mt-2 text-[11px] text-[#938F99]">
                                                    <span>MO #{bonus.mo_id}</span>
                                                    <span className="text-[#3b2a55]">•</span>
                                                    <span>{bonus.mc_odate || "-"}</span>
                                                    <span className="text-[#3b2a55]">•</span>
                                                    <span>{formatYieldBonusPercent(bonus.mc_percent)}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide text-[#a78bfa] bg-[#7c3aed]/15 border border-[#7c3aed]/30 whitespace-nowrap">
                                            {bonus.mc_status || "-"}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <>
                            {/* Progress label */}
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[#7c6a9a] text-xs"><span>{t("staking.rewardCapProgress") || "3x Max Cap Progress"}</span></span>
                                <span className="text-[#7c6a9a] text-xs">{userROIAndCapLive?.remainingMaxCap} / {userROIAndCapLive?.maxCap} USDT</span>
                            </div>

                            {/* Progress bar */}
                            <div className="w-full h-2.5 rounded-full overflow-hidden" style={{ background: 'rgba(124,58,237,0.15)' }}>
                                <div
                                    className="h-full rounded-full transition-all duration-700"
                                    style={{
                                        width: `${Math.min(100, (userROIAndCapLive?.remainingMaxCap / userROIAndCapLive?.maxCap) * 100)}%`,
                                        background: 'linear-gradient(90deg, #5b21b6 0%, #a855f7 100%)'
                                    }}
                                />
                            </div>
                        </>
                    )}

                </div>

                {!isYieldPage && (
                    <div className="grid grid-cols-2 gap-3">
                        {/* Pending Base */}
                        <div
                            className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                            style={{ background: 'rgba(124,58,237,0.08)' }}
                        >
                            <span
                                className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                            >
                                <img src={ImgTotalStaked} alt="pending" className="size-5 object-contain" />
                            </span>
                            <div>
                                <p className="text-[#938F99] text-[12px] leading-tight">{t("staking.pendingBase") || "Pending Base"}</p>
                                <p className="text-white font-bold text-xl leading-none mt-2">
                                    {userROIAndCapLive?.pendingBase ?? "0.00"}
                                </p>
                            </div>
                        </div>

                        {/* Pending Booster */}
                        <div
                            className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                            style={{ background: 'rgba(124,58,237,0.08)' }}
                        >
                            <span
                                className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                            >
                                <img src={ImgClaimable} alt="active" className="size-5 object-contain" />
                            </span>
                            <div>
                                <p className="text-[#938F99] text-[12px] leading-tight">{t("staking.pendingActive") || "Pending Booster"}</p>
                                <div className="flex items-end gap-2 flex-wrap mt-2">
                                    <p className="text-white font-bold text-xl leading-none">
                                        {userROIAndCapLive?.pendingActive ?? "0.00"}
                                    </p>
                                    <span
                                        className="text-[#7c6a9a] text-[10px] font-semibold px-2 py-0.5 rounded mb-0.5"
                                        style={{ background: 'rgba(255,255,255,0.08)' }}
                                    >
                                        {ranking > 0 ? `${ranking}%` : "0%"}
                                    </span>
                                </div>
                            </div>
                        )}
                        </div>

                        {/* Claimable Base */}
                        <div
                            className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                            style={{ background: 'rgba(124,58,237,0.08)' }}
                        >
                            <span
                                className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                            >
                                <img src={ImgTotalStaked} alt="pending" className="size-5 object-contain" />
                            </span>
                            <div>
                                <p className="text-[#938F99] text-[12px] leading-tight">{t("staking.confirmedBase") || "Claimable Base"}</p>
                                <p className="text-white font-bold text-xl leading-none mt-2">
                                    {userROIAndCapLive?.confirmedBase ?? "0.00"}
                                </p>
                            </div>
                        </div>

                        {/* Claimable Booster */}
                        <div
                            className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                            style={{ background: 'rgba(124,58,237,0.08)' }}
                        >
                            <span
                                className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                            >
                                <img src={ImgTotalStaked} alt="pending" className="size-5 object-contain" />
                            </span>
                            <div>
                                <p className="text-[#938F99] text-[12px] leading-tight">{t("staking.confirmedActive") || "Claimable Booster"}</p>
                                <p className="text-white font-bold text-xl leading-none mt-2">
                                    {userROIAndCapLive?.confirmedActive ?? "0.00"}
                                </p>
                            </div>
                        </div>

                        {/* Growth Bonus */}
                        <div
                            className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                            style={{ background: 'rgba(124,58,237,0.08)' }}
                        >
                            <span
                                className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                            >
                                <img src={ImgTotalStaked} alt="pending" className="size-5 object-contain" />
                            </span>
                            <div>
                                <p className="text-[#938F99] text-[12px] leading-tight">{t("common.growthBonus") || "Growth Bonus"}</p>
                                <p className="text-white font-bold text-xl leading-none mt-2">
                                    {userROIAndCapLive?.growthBonus ?? "0.00"}
                                </p>
                            </div>
                        </div>

                        {/* Consensus Bonus */}
                        <div
                            className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                            style={{ background: 'rgba(124,58,237,0.08)' }}
                        >
                            <span
                                className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                            >
                                <img src={ImgTotalStaked} alt="pending" className="size-5 object-contain" />
                            </span>
                            <div>
                                <p className="text-[#938F99] text-[12px] leading-tight">{t("common.consensusBonus") || "Consensus Bonus"}</p>
                                <p className="text-white font-bold text-xl leading-none mt-2">
                                    {userROIAndCapLive?.consensusBonus ?? "0.00"}
                                </p>
                            </div>
                        </div>

                        {/* Total ROI */}
                        <div
                            className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                            style={{ background: 'rgba(124,58,237,0.08)' }}
                        >
                            <span
                                className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                            >
                                <img src={ImgTotalStaked} alt="pending" className="size-5 object-contain" />
                            </span>
                            <div>
                                <p className="text-[#938F99] text-[12px] leading-tight">{t("staking.totalROI") || "Total ROI"}</p>
                                <p className="text-white font-bold text-xl leading-none mt-2">
                                    {userROIAndCapLive?.totalROI ?? "0.00"}
                                </p>
                            </div>
                        </div>

                        {/* Total Active */}
                        <div
                            className="rounded-xl p-4 border border-[#2a1a42] flex flex-col gap-2 transition-all duration-300 hover:shadow-[0_0_24px_rgba(124,58,237,0.45)] hover:border-[#7c3aed]/60"
                            style={{ background: 'rgba(124,58,237,0.08)' }}
                        >
                            <span
                                className="size-8 rounded-full border border-[#2a1a42] flex items-center justify-center"
                            >
                                <img src={ImgTotalStaked} alt="pending" className="size-5 object-contain" />
                            </span>
                            <div>
                                <p className="text-[#938F99] text-[12px] leading-tight">{t("staking.totalActive") || "Total Active"}</p>
                                <p className="text-white font-bold text-xl leading-none mt-2">
                                    {userROIAndCapLive?.totalActive ?? "0.00"}
                                </p>
                            </div>
                        </div>

                        {/*claim*/}
                        <div className="col-span-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowClaimModal(true);
                                }}
                                disabled={isLoadingClaimRewards}
                                className="h-14 w-full rounded-full text-white font-medium text-lg mt-2 opacity-100 hover:opacity-80 transition-all duration-300 ease-in-out cursor-pointer"
                                style={{ background: "#5B47CD" }}
                            >
                                {isLoadingClaimRewards ? (
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="animate-spin rounded-full size-5 border-b-2 border-white"></div>
                                        <span>{t("common.loading") || "Loading..."}</span>
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center gap-2">
                                        {t("staking.claim") || "Claim Rewards"}
                                        <span className="material-symbols-outlined text-lg">
                                            arrow_forward
                                        </span>
                                    </div>
                                )}
                            </button>
                        </div>

                    </div>
                )}

            </div>


            <AnimatePresence>
                {showRestakeSuccessModal && (
                    <motion.div
                        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowRestakeSuccessModal(false)}
                    >
                        <motion.div
                            className="w-full max-w-sm rounded-3xl bg-[#12091c] border border-emerald-400/40 p-7 shadow-2xl shadow-emerald-500/20 text-center"
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="mx-auto mb-5 flex size-20 items-center justify-center rounded-full bg-emerald-500/15 border-2 border-emerald-400">
                                <svg viewBox="0 0 24 24" className="size-11 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                    <path d="M5 12.5l4.2 4.2L19 7" />
                                </svg>
                            </div>
                            <h3 className="text-white text-2xl font-extrabold mb-2">Restake Successful</h3>
                            <p className="text-[#ad90cb] text-sm mb-6">Your aBnBUsdt balance has been added to Yield Staking.</p>
                            <button type="button" onClick={() => setShowRestakeSuccessModal(false)} className="h-12 w-full rounded-xl bg-[#5B47CD] text-white font-bold hover:bg-[#4C3BBA] transition-all">Done</button>
                        </motion.div>
                    </motion.div>
                )}

                {showRestakeModal && (
                    <motion.div
                        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !isRestaking && setShowRestakeModal(false)}
                    >
                        <motion.div
                            className="w-full max-w-sm rounded-3xl bg-[#12091c] border border-[#9770DD]/40 p-6 shadow-2xl shadow-[#5B47CD]/20"
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-white text-xl font-extrabold mb-2">Confirm Restake</h3>
                            <p className="text-[#ad90cb] text-sm mb-6">Restake your aBnBUsdt balance into Yield Staking?</p>
                            <div className="rounded-2xl bg-[#05010D] border border-[#2a1a42] p-4 mb-6">
                                <div className="flex justify-between text-sm">
                                    <span className="text-[#7c6a9a]">Restake Amount</span>
                                    <span className="text-white font-bold">{formatBalanceForDisplay(atokenBalance, 6)} aBnBUsdt</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button type="button" onClick={() => setShowRestakeModal(false)} disabled={isRestaking} className="h-12 rounded-xl border border-[#9770DD]/30 text-[#DFD2FE] font-bold hover:bg-white/10 transition-all disabled:opacity-50">Cancel</button>
                                <button type="button" onClick={confirmRestake} disabled={isRestaking} className="h-12 rounded-xl bg-[#5B47CD] text-white font-bold hover:bg-[#4C3BBA] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                                    {isRestaking && <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                                    {isRestaking ? "Processing..." : "Confirm Restake"}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {showAtokenWithdrawalModal && (
                    <motion.div
                        className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 px-4"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => !isWithdrawingAtoken && setShowAtokenWithdrawalModal(false)}
                    >
                        <motion.div
                            className="w-full max-w-sm rounded-3xl bg-[#12091c] border border-[#9770DD]/40 p-6 shadow-2xl shadow-[#5B47CD]/20"
                            initial={{ scale: 0.95, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, y: 20 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-white text-xl font-extrabold mb-2">{t("yield.confirmClaim") || "Confirm Claim"}</h3>
                            <p className="text-[#ad90cb] text-sm mb-6">{t("yield.confirmClaimMessage") || "Confirm claim all available aBnBUsdt amount?"}</p>
                            <div className="rounded-2xl bg-[#05010D] border border-[#2a1a42] p-4 mb-6">
                                <div className="flex justify-between text-sm">
                                    <span className="text-[#7c6a9a]">{t("yield.claimAmount") || "Claim Amount"}</span>
                                    <span className="text-white font-bold">{formatBalanceForDisplay(atokenBalance, 6)} aBnBUsdt</span>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <button type="button" onClick={() => setShowAtokenWithdrawalModal(false)} disabled={isWithdrawingAtoken} className="h-12 rounded-xl border border-[#9770DD]/30 text-[#DFD2FE] font-bold hover:bg-white/10 transition-all disabled:opacity-50">{t("yield.cancel") || t("common.cancel") || "Cancel"}</button>
                                <button type="button" onClick={confirmAtokenWithdrawal} disabled={isWithdrawingAtoken} className="h-12 rounded-xl bg-[#5B47CD] text-white font-bold hover:bg-[#4C3BBA] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                                    {isWithdrawingAtoken && <div className="size-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />}
                                    {isWithdrawingAtoken ? (t("yield.processing") || "Processing...") : (t("yield.confirm") || t("common.confirm") || "Confirm")}
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {showApproveModal && (
                    <div
                        className="fixed inset-0 bg-gray-950/60 flex items-center justify-center z-50 px-4"
                        onClick={handleCloseApprove}
                    >
                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.9 }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full max-w-md rounded-2xl border-2 border-[#9770DD]/60 bg-gradient-to-br from-[#050505]/95 to-black/95 p-8 shadow-2xl"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-2xl font-semibold text-white">
                                    {t("common.approvalRequired") || "Approval Required"}
                                </h2>
                                {!isApproving && (
                                    <button
                                        onClick={handleCloseApprove}
                                        className="size-8 rounded-full text-white hover:bg-white/10 flex items-center justify-center"
                                    >
                                        <FiX />
                                    </button>
                                )}
                            </div>

                            {approvalStatus === null && (
                                <>
                                    <p className="text-white/80 mb-6 leading-relaxed">
                                        {t("common.approvalMessage") || "Please approve USDT spending before staking."}
                                    </p>
                                    <div className="flex gap-3 justify-end">
                                        <button
                                            onClick={handleCloseApprove}
                                            disabled={isApproving}
                                            className="px-6 py-3 rounded-lg border border-white/30 text-white hover:bg-white/10 disabled:opacity-50"
                                        >
                                            {t("common.cancel") || "Cancel"}
                                        </button>
                                        <button
                                            onClick={approveStake}
                                            disabled={isApproving}
                                            className="px-6 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-[#9770DD] to-[#ff6b6b] hover:scale-105 transition-transform disabled:opacity-60"
                                        >
                                            {isApproving ? (t("common.approving") || "Approving...") : (t("common.approve") || "Approve")}
                                        </button>
                                    </div>
                                </>
                            )}

                            {approvalStatus === "pending" && (
                                <div className="text-center py-5">
                                    <div className="size-14 border-4 border-[#9770DD]/30 border-t-[#9770DD] rounded-full animate-spin mx-auto mb-4" />
                                    <p className="text-white text-lg font-semibold mb-2">
                                        {t("common.processingApproval") || "Processing Approval"}
                                    </p>
                                    <p className="text-white/70 text-sm">
                                        {t("common.confirmTransaction") || "Please confirm transaction in wallet"}
                                    </p>
                                </div>
                            )}

                            {approvalStatus === "success" && (
                                <div className="text-center py-5">
                                    <div className="size-14 rounded-full bg-green-500/20 text-green-400 text-3xl flex items-center justify-center mx-auto mb-4">✓</div>
                                    <p className="text-white text-lg font-semibold mb-2">
                                        {t("common.approvalSuccessful") || "Approval Successful"}
                                    </p>
                                    <p className="text-white/70 text-sm">
                                        {t("common.canPurchaseNow") || "You can continue staking now."}
                                    </p>
                                </div>
                            )}

                            {approvalStatus === "error" && (
                                <div className="text-center py-5">
                                    <div className="size-14 rounded-full bg-red-500/20 text-red-400 text-3xl flex items-center justify-center mx-auto mb-4">×</div>
                                    <p className="text-white text-lg font-semibold mb-2">
                                        {t("common.approvalFailed") || "Approval Failed"}
                                    </p>
                                    <p className="text-white/70 text-sm mb-5">
                                        {approvalError || t("common.tryAgain") || "Please try again"}
                                    </p>
                                    <div className="flex gap-3 justify-center">
                                        <button
                                            onClick={handleCloseApprove}
                                            className="px-6 py-3 rounded-lg border border-white/30 text-white hover:bg-white/10"
                                        >
                                            {t("common.close") || "Close"}
                                        </button>
                                        <button
                                            onClick={approveStake}
                                            className="px-6 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-[#9770DD] to-[#ff6b6b]"
                                        >
                                            {t("common.tryAgain") || "Try Again"}
                                        </button>
                                    </div>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {showStakeModal && (
                <div className="fixed inset-0 z-50 bg-gray-950/60 backdrop-blur-sm flex items-center justify-center px-4">
                    <div className="w-full max-w-sm rounded-2xl bg-[#1a1023] border border-[#9770DD]/40 p-6 shadow-xl shadow-[#9770DD]/20">
                        <div className="flex items-center gap-3 mb-3">
                            <FiRotateCcw className="size-5 text-[#DFD2FE]" />
                            <h3 className="text-white text-lg font-semibold">
                                {t("validation.stakingConfirmationTitle") || "Staking Confirmation"}
                            </h3>
                        </div>
                        <p className="text-sm text-[#ad90cb] mb-4">
                            {t("validation.stakingConfirmationDesc") || "Please confirm your staking transaction."}
                        </p>

                        <div className="rounded-xl border border-[#9770DD]/30 bg-[#241630]/30 px-4 py-3 mb-5">
                            <div className="text-xs text-[#ad90cb] mb-1">{t("staking.stakeAmount") || "Stake Amount"}</div>
                            <div className="text-white font-semibold text-lg">
                                {stakeAmount || "0"} <span className="text-[#9770DD] font-medium">{selectedToken === "VT Balance" ? "USD" : selectedToken}</span>
                            </div>
                        </div>

                        <div className="flex gap-3 justify-end">
                            <button
                                type="button"
                                onClick={() => setShowStakeModal(false)}
                                disabled={isLoadingStake}
                                className="px-6 py-3 rounded-lg border border-white/30 text-white hover:bg-white/10 disabled:opacity-50"
                            >
                                {t("common.cancel") || "Cancel"}
                            </button>
                            <button
                                type="button"
                                onClick={handleStake}
                                disabled={isLoadingStake || !account}
                                className="px-6 py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-[#9770DD] to-[#ff6b6b] hover:scale-[1.02] transition-transform disabled:opacity-60"
                            >
                                {t("staking.stakeNow") || "Confirm Stake"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showClaimModal && (
                <div className="fixed inset-0 z-50 bg-gray-950/60 backdrop-blur-sm flex items-center justify-center p-4 pb-24 sm:pb-6">
                    <div className="w-full max-w-md rounded-2xl bg-[#1a1023] border border-[#9770DD]/40 flex flex-col max-h-[80dvh] sm:max-h-[90dvh] shadow-xl shadow-[#9770DD]/20">
                        {/* Modal Header */}
                        <div className="flex items-center gap-3 p-4 sm:p-6 pb-2 sm:pb-3">
                            <span className="text-xl sm:text-2xl">⚠️</span>
                            <h3 className="text-white text-base sm:text-lg font-semibold">
                                {t("staking.claimConfirmationTitle") || "Claim Confirmation"}
                            </h3>
                        </div>

                        {/* Scrollable Content Area */}
                        <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-1 sm:py-2">
                            <div className="space-y-2 sm:space-y-4 text-[12px] sm:text-[13px] text-[#E8DEF8] bg-[#241630]/30 rounded-xl p-3 sm:p-4 border border-[#9770DD]/20">
                                <p className="font-medium text-[#DFD2FE]">
                                    {t("staking.claimConfirmationDesc1") || "Upon claiming, your rewards will be programmatically allocated as follows:"}
                                </p>
                                <ul className="list-disc pl-5 space-y-0.5 sm:space-y-1 text-[#ad90cb]">
                                    <li>{t("staking.claimConfirmationAlloc1") || "70% → claimable payout"}</li>
                                    <li>{t("staking.claimConfirmationAlloc2") || "30% → Automatically converted into ticket units"}</li>
                                </ul>

                                <div className="pt-1 sm:pt-2">
                                    <p className="font-semibold text-[#DFD2FE] mb-1 sm:mb-2">
                                        {t("staking.claimConfirmationLogicTitle") || "Ticket Conversion Logic:"}
                                    </p>
                                    <ul className="list-disc pl-5 space-y-0.5 sm:space-y-1 text-[#ad90cb] mb-1 sm:mb-2">
                                        <li>{t("staking.claimConfirmationLogic1") || "1 ticket = $10 (from the 30% allocation)"}</li>
                                        <li>{t("staking.claimConfirmationLogic2") || "Only full units are minted:"}</li>
                                    </ul>
                                    <div className="pl-6 space-y-0.5 sm:space-y-1 text-[11px] sm:text-xs text-[#ad90cb]">
                                        <p className="flex items-center gap-2"><span className="size-1.5 rounded-full bg-[#9770DD]"></span>{t("staking.claimConfirmationLogic2a") || "$10 → 1 ticket"}</p>
                                        <p className="flex items-center gap-2"><span className="size-1.5 rounded-full bg-[#9770DD]"></span>{t("staking.claimConfirmationLogic2b") || "$20 → 2 tickets"}</p>
                                    </div>
                                    <ul className="list-disc pl-5 space-y-0.5 sm:space-y-1 text-[#ad90cb] mt-1 sm:mt-2">
                                        <li>{t("staking.claimConfirmationLogic3") || "Residual value is not carried forward"}</li>
                                    </ul>
                                </div>

                                <div className="mt-2 sm:mt-4 p-2 sm:p-3 rounded-lg sm:rounded-xl bg-yellow-500/10 border border-yellow-500/20 text-yellow-200/90 text-[11px] sm:text-[13px] flex items-start gap-2 sm:gap-3">
                                    <span className="material-symbols-outlined text-yellow-500 text-base sm:text-lg flex-shrink-0">warning</span>
                                    <p>{t("staking.claimConfirmationLogic4") || "If the 30% allocation is below $10 → no ticket will be issued"}</p>
                                </div>

                                <p className="text-[10px] sm:text-xs text-[#ad90cb] italic pt-1 sm:pt-2 border-t border-[#9770DD]/20">
                                    {t("staking.claimConfirmationAck") || "By proceeding, you acknowledge and accept this allocation mechanism."}
                                </p>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="flex gap-3 justify-end p-4 sm:p-6 pt-2 sm:pt-4 mt-auto border-t border-[#9770DD]/10">
                            <button
                                type="button"
                                onClick={() => setShowClaimModal(false)}
                                disabled={isLoadingClaimRewards}
                                className="px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl border border-white/20 text-white hover:bg-white/10 disabled:opacity-50 transition-colors text-xs sm:text-sm font-medium"
                            >
                                {t("common.cancel") || "Cancel"}
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowClaimModal(false);
                                    claimRewards();
                                }}
                                disabled={isLoadingClaimRewards}
                                className="px-4 py-2 sm:px-5 sm:py-2.5 rounded-xl text-white font-semibold bg-gradient-to-r from-[#9770DD] to-[#7a1fd6] hover:shadow-[0_0_15px_rgba(151,112,221,0.4)] active:scale-[0.98] transition-all disabled:opacity-60 text-xs sm:text-sm flex items-center gap-2"
                            >
                                {t("common.confirm") || "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* New Process Claim Modal */}
            <AnimatePresence>
                {showProcessClaimModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-0 z-[100] bg-[#05010D] text-white"
                    >
                        <div className="max-w-[550px] w-full mx-auto min-h-screen flex flex-col relative">
                            {/* Header */}
                            <div className="flex items-center gap-4 p-5">
                                <button onClick={() => setShowProcessClaimModal(false)} className="material-symbols-outlined text-white text-2xl">arrow_back</button>
                                <h2 className="text-xl font-medium">{t("staking.claimReward")}</h2>
                            </div>

                            {/* Content */}
                            <div
                                className="flex-1 flex flex-col items-center p-6 relative overflow-y-auto max-h-[calc(90vh-100px)] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#888]/40 hover:[&::-webkit-scrollbar-thumb]:bg-[#888]/60 [&::-webkit-scrollbar-thumb]:rounded-full"
                            >
                                {processClaimStep === 1 ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col gap-3 items-center w-full max-w-[550px] mx-auto my-auto"
                                    >
                                        <div className="relative size-[140px] mb-12">
                                            <div className="absolute inset-0 rounded-full border-[4px] border-[#2A1A42]"></div>
                                            <div className="absolute inset-0 rounded-full border-[4px] border-transparent border-t-[#A855F7] border-r-[#A855F7] animate-[spin_1.5s_linear_infinite] drop-shadow-[0_0_15px_rgba(168,85,247,0.6)]"></div>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[60px] text-[#A855F7] drop-shadow-[0_0_15px_rgba(168,85,247,0.8)]">hourglass_empty</span>
                                            </div>
                                        </div>
                                        <h3 className="text-[28px] font-semibold mb-3">{t("staking.processing")}</h3>
                                        <p className="text-[#938F99] text-center text-sm leading-relaxed max-w-[240px]">
                                            {t("staking.pleaseWaitWhileWeProcessYourClaim")}                                        </p>

                                        {/* Bottom Warning */}
                                        <div className="bg-[#110A1F] rounded-2xl p-4 flex items-center gap-3 border border-[#2A1A42] w-full mx-auto">
                                            <span className="material-symbols-outlined text-[#8B5CF6]">gpp_good</span>
                                            <p className="text-[13px] text-[#938F99] leading-tight">{t("staking.thisMayTakeAwhilePleaseDoNotClose")}</p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col items-center w-full max-w-md mx-auto my-auto"
                                    >
                                        <div className="size-[100px] rounded-full bg-gradient-to-b from-[#A855F7] to-[#4C1D95] p-[4px] mb-6 shadow-[0_0_40px_rgba(168,85,247,0.4)] mt-4">
                                            <div className="w-full h-full bg-[#1A0B2E] rounded-full flex items-center justify-center shadow-[inset_0_0_20px_rgba(168,85,247,0.5)]">
                                                <img src={ImgClaimCheck} alt="" style={{ width: '100px', height: '100px' }} />
                                            </div>
                                        </div>

                                        <h3 className="text-[28px] font-semibold mb-3 text-center">{t("staking.rewardsClaimed")}</h3>
                                        <p className="text-[#938F99] text-center text-sm mb-6 max-w-[280px] leading-relaxed">
                                            {t("staking.yourRewardsHaveBeenClaimed")}
                                        </p>

                                        {/* Receipt Card */}
                                        <div className="w-full bg-transparent border border-[#2A1A42] rounded-3xl p-6 mb-8 relative overflow-hidden">
                                            <p className="text-[#938F99] text-center text-[15px] mb-2">{t("staking.youHaveReceived")}</p>
                                            <p className="text-white text-center text-[40px] font-bold mb-6 flex justify-center items-baseline gap-2">
                                                {claimableRewardValue} <span className="text-[16px] font-medium text-[#938F99]">aBnbUSDT</span>
                                            </p>

                                            <div className="flex justify-center mb-8 relative">
                                                <div className="absolute inset-0 bg-[#A855F7] opacity-20 blur-2xl rounded-full"></div>
                                                <img src={Img3dTotalStaked} className="size-[125px] object-contain relative z-10" alt="Tokens" />
                                            </div>

                                            <div className="flex justify-between border-t border-[#2A1A42] pt-5">
                                                <div className="flex items-start gap-3 w-1/2">
                                                    <span className="material-symbols-outlined text-[#A855F7] text-2xl">schedule</span>
                                                    <div>
                                                        <p className="text-[#5b4a75] text-[11px] font-medium mb-1">{t("staking.claimTime")}</p>
                                                        <p className="text-white text-[12px] font-bold whitespace-nowrap">{dayjs().format("MMM DD, YYYY HH:mm [UTC]")}</p>
                                                    </div>
                                                </div>
                                                <div className="w-px bg-[#2A1A42] self-stretch mx-2"></div>
                                                <div className="flex items-start gap-3 w-1/2 pl-2">
                                                    <span className="material-symbols-outlined text-[#A855F7] text-2xl flex-shrink-0" style={{ fontVariationSettings: "'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24" }}>deployed_code</span>
                                                    <div>
                                                        <p className="text-[#5b4a75] text-[11px] font-medium mb-1">{t("common.network")}</p>
                                                        <p className="text-white text-[12px] font-bold">aBnBUsdt</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* What's Next */}
                                        <div className="w-full text-left mt-auto mb-6">
                                            <h4 className="text-[22px] font-semibold text-white mb-4">{t('staking.whatNext')}</h4>
                                            <div className="flex items-center justify-between cursor-pointer group">
                                                <div className="flex items-center gap-4">
                                                    <div className="size-12 rounded-full bg-[#1A0B2E] border border-[#2A1A42] flex items-center justify-center group-hover:border-[#A855F7] transition-colors">
                                                        <img src={ImgTotalStaked} className="size-6" alt="Stake" />
                                                    </div>
                                                    <div>
                                                        <p className="text-white text-[15px] font-bold mb-0.5">{t('staking.stakeMore')}</p>
                                                        <p className="text-[#5b4a75] text-[13px]">{t('staking.stakeMoreTokenToEarnMoreReward')}</p>
                                                    </div>
                                                </div>
                                                <span onClick={() => { setShowProcessClaimModal(false) }} className="material-symbols-outlined text-[#938F99] group-hover:text-white transition-colors text-2xl">chevron_right</span>
                                            </div>
                                        </div>

                                    </motion.div>

                                )}
                            </div>

                            {/* Done Button for Step 2 */}
                            {processClaimStep === 2 && (
                                <div className="p-5 w-full max-w-md mx-auto mt-auto flex-shrink-0">
                                    <button
                                        onClick={() => setShowProcessClaimModal(false)}
                                        className="w-full h-14 rounded-full bg-[#5B47CD] text-white font-bold text-lg hover:bg-[#4C3BBA] active:scale-[0.98] transition-all"
                                    >
                                        {t('common.done')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* New Process Stake Modal */}
            <AnimatePresence>
                {showProcessStakeModal && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        className="fixed inset-0 z-[100] bg-[#05010D] text-white overflow-y-auto"
                    >
                        <div className="max-w-[550px] w-full mx-auto min-h-screen flex flex-col relative">
                            {/* Header */}
                            <div className="flex items-center gap-4 p-5">
                                <button onClick={() => setShowProcessStakeModal(false)} className="material-symbols-outlined text-white text-2xl">arrow_back</button>
                                <h2 className="text-xl font-medium"></h2>
                            </div>

                            {/* Content */}
                            <div className="flex-1 flex flex-col items-center p-6 relative overflow-y-auto max-h-[calc(90vh-100px)] [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-[#888]/40 hover:[&::-webkit-scrollbar-thumb]:bg-[#888]/60 [&::-webkit-scrollbar-thumb]:rounded-full">
                                {processStakeStep === 1 ? (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col gap-3 items-center w-full max-w-[550px] mx-auto my-auto"
                                    >
                                        <div className="relative size-[140px] mb-12">
                                            <div className="absolute inset-0 rounded-full border-[4px] border-[#2A1A42]"></div>
                                            <div className="absolute inset-0 rounded-full border-[4px] border-transparent border-t-[#A855F7] border-r-[#A855F7] animate-[spin_1.5s_linear_infinite] drop-shadow-[0_0_15px_rgba(168,85,247,0.6)]"></div>
                                            <div className="absolute inset-0 flex items-center justify-center">
                                                <span className="material-symbols-outlined text-[60px] text-[#A855F7] drop-shadow-[0_0_15px_rgba(168,85,247,0.8)]">hourglass_empty</span>
                                            </div>
                                        </div>
                                        <h3 className="text-[28px] font-semibold mb-3">{t('staking.processing')}</h3>
                                        <p className="text-[#938F99] text-center text-sm leading-relaxed max-w-[240px]">
                                            {t('staking.pleaseWaitWhileWeProcessYourStake')}
                                        </p>

                                        {/* Bottom Warning */}
                                        <div className="bg-[#110A1F] rounded-2xl p-4 flex items-center gap-3 border border-[#2A1A42] w-full mx-auto mt-4">
                                            <span className="material-symbols-outlined text-[#8B5CF6]">gpp_good</span>
                                            <p className="text-[13px] text-[#938F99] leading-tight">{t('staking.thisMayTookFewSecondsPleaseDoNotCloseThisPage')}</p>
                                        </div>
                                    </motion.div>
                                ) : (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.9 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        className="flex flex-col items-center w-full max-w-md mx-auto my-auto w-[100%]"
                                    >
                                        <div className="relative mb-4 flex justify-center mt-2">
                                            <div className="absolute inset-0 bg-[#A855F7] opacity-20 blur-3xl rounded-full size-[220px] mx-auto"></div>
                                            <div className="relative inline-block">
                                                <img src={ImgStakingActive} className="size-[220px] object-contain relative z-10" alt="Staked Asset" />
                                            </div>
                                        </div>

                                        <h3 className="text-[28px] font-semibold mb-2 text-center text-white">{t('staking.stakeSuccess')}</h3>
                                        <p className="text-[#938F99] text-center text-sm mb-6 max-w-[280px] leading-relaxed">
                                            {t('staking.yourAssetAreNowStakeAndStartEarningRewards')}
                                        </p>

                                        {/* Receipt Card */}
                                        <div className="w-full bg-[#05010D] border border-[#2A1A42] rounded-3xl p-5 mb-5 relative overflow-hidden">
                                            <div className="space-y-4">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[#938F99] text-[13px] font-medium">{t('common.token')}</span>
                                                    <div className="flex items-center gap-2">
                                                        {selectedToken === "USDT" ? (
                                                            <img src={IconTokenUsdt} className="size-[18px] rounded-full" alt="USDT" />
                                                        ) : selectedToken === "aBnBusdt" ? (
                                                            <span className="size-[18px] rounded-full flex items-center justify-center text-[8px] font-bold text-[#5b47cd] border border-[#7c3aed]/60 bg-[rgb(200,168,255)]">aB</span>
                                                        ) : (
                                                            <span className="size-[18px] rounded-full flex items-center justify-center text-[8px] font-bold text-white border border-[#9770DD]/60 bg-gradient-to-r from-[#9770DD] to-[#ff6b6b]">VT</span>
                                                        )}
                                                        <span className="text-white font-bold text-[15px]">{selectedToken === "VT Balance" ? "USD" : selectedToken}</span>
                                                    </div>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[#938F99] text-[13px] font-medium">{t("common.amount")}</span>
                                                    <span className="text-white font-bold text-[15px]">{stakeAmount || "1,400"} {selectedToken === "VT Balance" ? "USD" : selectedToken}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-[#938F99] text-[13px] font-medium">{t("common.startDateTime")}</span>
                                                    <span className="text-white font-bold text-[15px]">{dayjs().format("MMM DD, YYYY")}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Warning */}
                                        <div className="bg-[#110A1F] rounded-2xl p-4 flex items-center gap-3 border border-[#2A1A42] w-full mx-auto mb-2">
                                            <span className="material-symbols-outlined text-[#8B5CF6]">gpp_good</span>
                                            <p className="text-[12px] text-[#938F99] leading-tight">{t('staking.thisMayTookFewSecondsPleaseDoNotCloseThisPage')}</p>
                                        </div>
                                    </motion.div>
                                )}
                            </div>

                            {/* Buttons for Step 2 */}
                            {processStakeStep === 2 && (
                                <div className="px-6 pb-6 pt-2 w-full max-w-md mx-auto mt-auto flex-shrink-0 flex flex-col gap-3">
                                    <button
                                        onClick={handleCoseProcessStakeModal}
                                        className="w-full h-14 rounded-full bg-[#5B47CD] text-white font-bold text-[17px] hover:bg-[#4C3BBA] active:scale-[0.98] transition-all shadow-[0_0_20px_rgba(91,71,205,0.4)]"
                                    >
                                        {t('staking.viewPortfolio')}
                                    </button>
                                    <button
                                        onClick={() => {
                                            handleCoseProcessStakeModal();
                                            window.scrollTo({ top: 0, behavior: 'smooth' });
                                        }}
                                        className="w-full h-14 rounded-full bg-transparent border border-[#2A1A42] text-white font-bold text-[17px] hover:bg-[#2A1A42]/50 active:scale-[0.98] transition-all"
                                    >
                                        {t('staking.stakeMore')}
                                    </button>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
